import React, { useState, useEffect } from "react";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import "react-confirm-alert/src/react-confirm-alert.css";
import { confirmAlert } from "react-confirm-alert";
// import { createClient } from "@supabase/supabase-js";
import supabase from "../supabaseClient";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { createPortal } from "react-dom";
import { checkJobOrderExists } from "../utils/jobOrderGenerator";
import {
  getEditableShipmentStatuses,
  getShipmentProgressStepIndex,
} from "../utils/shipmentProgress";
import { matchesShipmentStatusFilter } from "../utils/shipmentManagementStatus";
import api, { API_BASE_URL } from "../config/api";
import { matchesDashboardDateFilter } from "../utils/dashboardDateFilter";
import { fetchAllSupabaseRows } from "../utils/supabasePagination";
import { DOCUMENT_SECTIONS } from "../utils/documentSections";
import DocumentSectionTabs from "./DocumentSectionTabs";
import { openDocumentGallery } from "../utils/openDocumentGallery";

dayjs.extend(customParseFormat);

const SHIPMENTS_PER_PAGE = 7;

// ---------------------- KPI STATUS CALCULATION ----------------------
const getKPIStatus = (shipment) => {
  if (!shipment) return null;

  const shipmentType = shipment.shipment_type;

  // Only calculate KPI for AIR, SEA, and BROKERAGE
  if (!["AIR", "SEA", "BROKERAGE"].includes(shipmentType)) {
    return null;
  }

  /**
   * 🔒 NORMALIZE REMARKS
   * - If array → use as-is
   * - If string / null → convert to empty array
   */
  const remarksArray = Array.isArray(shipment.remarks) ? shipment.remarks : [];

  // 🔹 Find "Arrival at Destination" remark
  const arrivalRemark = remarksArray.find(
    (r) => r?.status === "Arrival at Destination"
  );

  if (!arrivalRemark || !arrivalRemark.aad) {
    return null;
  }

  // 🔹 Parse arrival date
  let arrivalDate = dayjs(arrivalRemark.aad);

  if (!arrivalDate.isValid()) {
    arrivalDate = dayjs(arrivalRemark.aad, [
      "YYYY-MM-DDTHH:mm:ss",
      "YYYY-MM-DDTHH:mm",
      "YYYY-MM-DDTHH:mm:ss.SSSZ",
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DD HH:mm",
    ]);
  }

  if (!arrivalDate.isValid()) {
    return null;
  }

  // 🔹 Determine completion date
  let completionDate;

  if (shipment.status === "Delivered" || shipment.status === "Turnover") {
    const completionRemark = remarksArray.find(
      (r) => r?.status === "Delivered" || r?.status === "Turnover"
    );

    completionDate = completionRemark?.date
      ? dayjs(completionRemark.date)
      : dayjs();
  } else {
    completionDate = dayjs();
  }

  // 🔹 Calculate KPI days
  const daysDiff = completionDate.diff(arrivalDate, "day");

  if (daysDiff < 0) {
    return {
      status: "On Time",
      bgColor: "bg-green-100",
    };
  }

  // 🔹 AIR KPI
  if (shipmentType === "AIR") {
    if (daysDiff <= 2) {
      return {
        status: "On Time",
        bgColor: "bg-green-100",
      };
    }
    return {
      status: "Delayed",
      bgColor: "bg-red-100",
    };
  }

  // 🔹 SEA & BROKERAGE KPI
  if (shipmentType === "SEA" || shipmentType === "BROKERAGE") {
    if (daysDiff <= 5) {
      return {
        status: "On Time",
        bgColor: "bg-green-100",
      };
    }
    if (daysDiff <= 10) {
      return {
        status: "Slightly Delayed",
        bgColor: "bg-orange-200",
      };
    }
    return {
      status: "Delayed",
      bgColor: "bg-red-100",
    };
  }

  return null;
};

// ----------------------- STATUS STYLES -----------------------
const statusStyles = {
  "Order Placed":
    "bg-gray-200 text-gray-700 border px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap",
  "In Warehouse":
    "bg-blue-200 text-blue-700 border px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap",
  "Departure at Origin":
    "bg-orange-200 text-orange-700 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap",
  "In Transit":
    "bg-purple-200 text-purple-700 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap",
  "Arrival at Destination":
    "bg-yellow-200 text-yellow-700 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap",
  "Custom Clearance":
    "bg-teal-200 text-teal-700 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap",
  Delivered:
    "bg-green-200 text-green-700 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap",
  Turnover:
    "bg-teal-200 text-teal-700 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap",
};

const getStatusClass = (status, shipment = null) => {
  // For AIR EXPORT with DOOR-PORT or PORT-PORT, "Arrival at Destination" is completed (green)
  if (
    shipment &&
    status === "Arrival at Destination" &&
    shipment.shipment_type === "AIR" &&
    (shipment.shipment_mode === "EXPORT" || shipment.shipment_mode === "EXPORT-EVENT") &&
    (shipment.delivery_mode === "DOOR-PORT" || shipment.delivery_mode === "PORT-PORT")
  ) {
    return "bg-green-200 text-green-700 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap";
  }

  return (
    statusStyles[status] ||
    "bg-gray-100 text-gray-500 px-2 py-1 rounded-full text-xs font-medium"
  );
};

// ---------------------- SHIPMENT TYPE STYLES ----------------------
const shipmentTypeStyles = {
  AIR: "bg-amber-300 text-amber-800 px-3 py-1 rounded-full text-xs font-medium",
  SEA: "bg-sky-300 text-sky-800 px-3 py-1 rounded-full text-xs font-medium",
  LAND: "bg-green-300 text-green-800 px-3 py-1 rounded-full text-xs font-medium",
  DOMESTIC:
    "bg-purple-300 text-purple-800 px-3 py-1 rounded-full text-xs font-medium",
  BROKERAGE:
    "bg-orange-400 text-orange-900 px-3 py-1 rounded-full text-xs font-medium",
};

const getShipmentTypeClass = (type) => {
  return (
    shipmentTypeStyles[type] ||
    "bg-gray-200 text-gray-600 px-3 py-1 rounded-full text-xs font-medium"
  );
};

const ShipmentTable = ({
  onRowClick,
  role,
  filterValue,
  selectedYear = "all",
  selectedMonth = "all",
  selectedShipmentType = "all",
}) => {
  const user = useSelector((state) => state.auth.user);
  const userId = user?.user_id ?? user?.id;
  const [mounted, setMounted] = useState(false);
  const [portalContainer, setPortalContainer] = useState(null);

  // Add this useEffect to properly initialize the portal container
  useEffect(() => {
    setMounted(true);
    setPortalContainer(document.body);

    return () => setMounted(false);
  }, []);
  const [fetchEmployees, setFetchEmployees] = useState(null);
  // const [transferNames, setTransferNames] = useState({});
  const [jobOrder, setjobOrder] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [editableStatus, setEditableStatus] = useState("");
  const [editableTranserTo, setEditableTransferTo] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [newRemarkInputMode, setNewRemarkInputMode] = useState("predefined");
  const [newSelectedRemarkOption, setNewSelectedRemarkOption] = useState("");
  const [additionalRemarkInfo, setAdditionalRemarkInfo] = useState("");
  const [remarkDate, setRemarkDate] = useState("");
  const [manualRemarkText, setManualRemarkText] = useState("");
  const [remarkFootnote, setRemarkFootnote] = useState("");
  const [wayBill, setWayBill] = useState("");
  const [receivedDate, setReceivedDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [loading2, setLoading2] = useState(false); // Loading state to show spinner
  const [savingStatus, setSavingStatus] = useState(false); // Loading state for saving status
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState(filterValue); // State to track checkbox selection
  const [shipmentsPage, setShipmentsPage] = useState(1);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedShipmentForUpload, setSelectedShipmentForUpload] =
    useState(null);
  const [file, setFile] = useState(null);
  const [department, setDepartment] = useState(null);

  const [documentTitle, setDocumentTitle] = useState("");
  const [documentSection, setDocumentSection] = useState("");
  const [actualDepartureDate, setActualDepartureDate] = useState("");
  const [actualArrivalDate, setActualArrivalDate] = useState("");
  const [selectedShipmentForView, setSelectedShipmentForView] = useState(null); // For viewing the selected shipment
  const [viewModalOpen, setViewModalOpen] = useState(false); // Rename the modal state to viewModalOpen
  const [documents, setDocuments] = useState([]); // State to hold documents
  const [activeDocumentSection, setActiveDocumentSection] = useState("all");
  const [sendingDocumentKey, setSendingDocumentKey] = useState(null); // Guards against double-send
  const [activeTab, setActiveTab] = useState("shipper");
  const [currentPage, setCurrentPage] = useState(1); // Track the current page
  const documentsPerPage = 5;

  // Calculate the index of the first and last document on the current page
  const indexOfLastDocument = currentPage * documentsPerPage;
  const indexOfFirstDocument = indexOfLastDocument - documentsPerPage;
  const [transitType, setTransitType] = useState(""); // "Arrival" or "Departure"
  const [transitDate, setTransitDate] = useState(""); // Selected date

  // Slice the documents array to show only the documents for the current page
  const filteredDocuments = activeDocumentSection === "all" ? documents : documents.filter((doc) => doc.document_section === activeDocumentSection);
  const currentDocuments = filteredDocuments.slice(indexOfFirstDocument, indexOfLastDocument);
  const [activeRow, setActiveRow] = React.useState(null);
  // Removed unused brokerageRemarksHistory state
  const [corporateClients, setCorporateClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  // Editable Fields
  const editableShipperFields = [
    "Name",
    "Country",
    "City",
    "Building",
    "Email",
    "Phone",
    "Street",
    "Landmark",
  ];
  const editableReceiverFields = [
    "Name",
    "Country",
    "City",
    "Building",
    "Email",
    "Phone",
    "Street",
    "Landmark",
  ];
  const editableShipmentFields = [
    "shipment_type",
    "shipment_value",
    "shipment_mode",
    "shipment_packages",
    "shipment_instructions",
    "etd",
    "eta",
    // Add the SEA-specific fields
    "shipment_terms",
    "shipment_containers",
    "shipment_volume",
    "shipment_grossweight",
    // Add the AIR-specific fields
    "shipment_chargeableweight", // For AIR mode
    "shipment_grossweight", // For AIR mode
    "shipment_dimensions", // For AIR mode
    "delivery_mode", // AIR and SEA
  ];

  const remarksOptions = [
    "SHIPMENT ALLOCATION",
    "PRE-ALERT",
    "MANIFEST",
    "DRAFT COMPUTATION OF DUTIES & TAXES",
    "SHIPPING LINE FORMALITIES",
    "LODGEMENT",
    "SHIPPING LINE FORMALITIES",
    "FORWARDER FORMALITIES",
    "RELEASED FROM PORT WAREHOUSE",
    "PORTAL SUBMISSION",
    "POST FILING",
    "SHIPPING LINE FORMALITIES",
    "FORWARDER FORMALITIES",
    "RELEASED FROM PORT WAREHOUSE",
    "AIRLINE WAREHOUSE FORMALITIES",
    "RELEASED FROM AIRPORT WAREHOUSE",
    "ONLINE RELEASE SYSTEM (OLRS)",
    "PAYMENT OF PORT CHARGES",
    "DELIVERED",
    "TURNOVER TO CONSIGNEE’S DESIGNATED TRUCKER",
  ];

  const formatRemarkDateLabel = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const month = date.toLocaleString("en-US", { month: "long" }).toUpperCase();
    return `${month} ${date.getDate()}`;
  };

  const buildShipmentRemarkPreview = ({
    dateValue = "",
    remarkText = "",
    additionalInfo = "",
    footnote = "",
  }) => {
    const lines = [];
    const dateLabel = formatRemarkDateLabel(dateValue);
    if (dateLabel) lines.push(dateLabel);
    if (remarkText) lines.push(remarkText);
    if (additionalInfo) lines.push(additionalInfo);
    if (footnote) lines.push(`Footnote: ${footnote}`);
    return lines.join("\n").trim();
  };

  const renderShipmentRemarkPreview = (previewText) =>
    String(previewText || "")
      .split("\n")
      .filter(Boolean)
      .map((line, index) => (
        <div
          key={`${line}-${index}`}
          className={line.startsWith("Footnote:") ? "italic text-slate-600" : ""}
        >
          {line}
        </div>
      ));

  const getVisibleShipmentRemarkLines = (remarkText = "") =>
    String(remarkText || "")
      .split("\n")
      .filter(
        (line) =>
          line &&
          !(user?.role === "Corporate" && line.startsWith("Footnote:"))
      );

  const renderVisibleShipmentRemark = (remarkText = "") =>
    getVisibleShipmentRemarkLines(remarkText).map((line, index) => (
      <React.Fragment key={`${line}-${index}`}>
        {line}
        <br />
      </React.Fragment>
    ));

  const renderSavedShipmentRemark = (remark) => {
    const previewText = buildShipmentRemarkPreview({
      remarkText: getVisibleShipmentRemarkLines(remark?.remark).join("\n"),
      footnote: user?.role === "Corporate" ? "" : remark?.footnote || "",
    });

    return renderShipmentRemarkPreview(previewText || "No additional remarks");
  };
  // ---------------------- Use Effects ----------------------
  // Removed unused brokerage remarks fetch that was causing 404 errors
  // useEffect(() => {
  //   if (selectedShipment?.shipment_id) {
  //     fetch(`/api/brokerage-remarks/${selectedShipment.shipment_id}`)
  //       .then((res) => res.json())
  //       .then((data) => setBrokerageRemarksHistory(data))
  //       .catch((err) =>
  //         console.error("Error fetching brokerage remarks:", err)
  //       );
  //   }
  // }, [selectedShipment]);
  useEffect(() => {
    const shipmentChannel = supabase
      .channel("public:shipments")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "shipments" },
        (payload) => {
          setShipments((prev) => [
            payload.new,
            ...prev.filter((shipment) => shipment.shipment_id !== payload.new.shipment_id),
          ]);
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "shipments" },
        (payload) => {
          setShipments((prev) =>
            prev.map((shipment) =>
              shipment.shipment_id === payload.new.shipment_id
                ? payload.new
                : shipment
            )
          );
        }
      )
      .on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "shipments" },
        (payload) => {
          setShipments((prev) =>
            prev.filter(
              (shipment) => shipment.shipment_id !== payload.old.shipment_id
            )
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(shipmentChannel);
    };
  }, []);

  // Fetch corporate clients from departments API
  useEffect(() => {
    const fetchCorporateClients = async () => {
      try {
        const response = await api.get(
          `${API_BASE_URL}/api/departments?type=corporate&status=active`,
          { withCredentials: true }
        );
        const clients = response.data.departments || [];
        setCorporateClients(clients.map((c) => c.name));
      } catch (error) {
        console.error("Error fetching corporate clients:", error);
      }
    };

    fetchCorporateClients();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId || !user?.role) return;

      setLoading(true);

      try {
        let corporateClientFilter = "";

        if (user.role === "Corporate") {
          const { data: userData, error: userError } = await supabase
            .from("users")
            .select("department")
            .eq("user_id", userId)
            .single();

          console.log(userData);

          if (userError || !userData) {
            console.error("❌ Error fetching user department:", userError);
            setLoading(false);
            return;
          }

          const department = userData.department;
          console.log("✅ Department:", department);

          // Filter shipments by corporate client (department) for all corporate users
          if (department) {
            corporateClientFilter = department;
            console.log(
              `🔍 Filtering shipments for corporate client: ${department}`
            );
          }
        }

        const { data: shipmentsData, error: shipmentsError } =
          await fetchAllSupabaseRows(() => {
            let shipmentQuery = supabase
              .from("shipments")
              .select("*")
              .order("date", { ascending: false, nullsFirst: false })
              .order("shipment_id", { ascending: false });

            if (corporateClientFilter) {
              shipmentQuery = shipmentQuery.eq("corporate_client", corporateClientFilter);
            }

            return shipmentQuery;
          });

        if (shipmentsError) {
          console.error("❌ Error fetching shipments:", shipmentsError);
        } else {
          setShipments(shipmentsData);

          // ✅ Log each shipment_id and corporate_client
          // console.log("📦 Shipments:");
          // shipmentsData.forEach((shipment) => {
          //   console.log(
          //     `→ ${shipment.job_order} | Corporate Client: ${shipment.corporate_client}`
          //   );
          // });
        }
      } catch (err) {
        console.error("❌ Unexpected error:", err);
      }

      setLoading(false);
      fetchEmployeesData();
    };

    fetchData();
  }, [userId, user?.role]);

  useEffect(() => {
    if (viewModalOpen) {
      document.body.classList.add("overflow-hidden"); // Disable scrolling
    } else {
      document.body.classList.remove("overflow-hidden"); // Enable scrolling
    }

    return () => document.body.classList.remove("overflow-hidden"); // Cleanup
  }, [viewModalOpen]);
  useEffect(() => {
    if (isDetailsModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isDetailsModalOpen]);
  useEffect(() => {
    if (isHistoryModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isHistoryModalOpen]);

  // ---------------------- END of useEffects ----------------------
  const openHistoryModal = (shipment) => {
    setSelectedShipment(shipment);
    setIsHistoryModalOpen(true);
    // setEditableStatus(false);
  };
  const parseRemarks = () => {
    if (!selectedShipment?.remarks) return [];
    return selectedShipment.remarks.map((r) => ({
      ...r,
      date: r.date || r.updatedAt || r.atd || r.aad, // fallback if needed
    }));
  };
  const openDetailsModal = (shipment) => {
    setSelectedShipment(shipment);
    setIsDetailsModalOpen(true);
  };

  const closeDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedShipment(null);
  };

  // ===================Handle brokerage field change (Process for Brokerage)=================
  const [draftBrokerageRemarks, setDraftBrokerageRemarks] = useState({
    brokerage_title: "",
    brokerage_input: "",
    history: [], // Keeps track of all the changes
  });
  const handleBrokerageFieldChange = (field, value) => {
    setDraftBrokerageRemarks((prev) => {
      return {
        ...prev,
        [field]: value, // Track changes in the draft state
      };
    });
  };
  const handleSaveBrokerageField = async () => {
    const { brokerage_title, brokerage_input } = draftBrokerageRemarks;

    // Only save if there's any change
    if (brokerage_title || brokerage_input) {
      try {
        // Fetch existing remarks from the database
        const { data: existingRemarks, error: fetchError } = await supabase
          .from("shipments")
          .select("brokerage_remarks")
          .eq("shipment_id", selectedShipment.shipment_id)
          .single(); // Ensure only one record is fetched

        if (fetchError) {
          console.error(
            "Error fetching existing brokerage remarks:",
            fetchError
          );
          return;
        }

        const currentRemarks = existingRemarks?.brokerage_remarks || [];

        // Create the new history entry
        const newEntry = {
          timestamp: new Date().toISOString(),
          brokerage_title,
          brokerage_input,
        };

        // Merge existing remarks with the new entry
        const updatedHistory = [...currentRemarks, newEntry];

        // Save the updated history back to the database
        const { error: updateError } = await supabase
          .from("shipments")
          .update({
            brokerage_remarks: updatedHistory,
          })
          .eq("shipment_id", selectedShipment.shipment_id);

        if (updateError) {
          console.error("Error saving brokerage remarks:", updateError);
        } else {
          // console.log('Brokerage remarks saved successfully.');

          // Update the local state
          setDraftBrokerageRemarks((prev) => ({
            ...prev,
            history: updatedHistory,
          }));

          // Optionally, reset the input fields
          setDraftBrokerageRemarks({
            brokerage_title: "",
            brokerage_input: "",
            history: updatedHistory,
          });
        }
      } catch (error) {
        console.error("Unexpected error saving brokerage remarks:", error);
      }
    }
  };
  // Function to handle changes in Shipper, Receiver and shipment fields and save to database
  const handleFieldChange = (section, field, value) => {
    const fieldKey = field.toLowerCase(); // Simplified to match the column directly

    // console.log(`Changing field: ${fieldKey}, New value: ${value}`);
    setSelectedShipment((prev) => {
      const updatedShipment = {
        ...prev,
        [fieldKey]: value,
      };
      // console.log('Updated selectedShipment state:', updatedShipment);
      return updatedShipment;
    });
  };

  const handleSaveField = async (section, field, value) => {
    const fieldKey = field.toLowerCase(); // Ensure this matches your database column

    // Check for duplicate job order before saving
    if (fieldKey === "job_order" && value && value.trim()) {
      try {
        const exists = await checkJobOrderExists(value.trim());
        if (exists) {
          toast.error(
            "This Job Order already exists. Please use a different one.",
            {
              position: "top-right",
            }
          );
          return;
        }
      } catch (err) {
        console.error("Error checking job order:", err);
      }
    }

    // Update the database with the corrected column name
    const { data, error } = await supabase
      .from("shipments")
      .update({ [fieldKey]: value })
      .eq("shipment_id", selectedShipment.shipment_id);

    // if (error) {
    //   console.error(`Error updating ${fieldKey} field:`, error);
    // } else {
    //   console.log(`${fieldKey} field updated successfully:`, data);
    // }
  };

  // ======================Function to handle shipper field change=========================
  const handleShipperFieldChange = (field, value) => {
    const fieldKey = `shipper_${field.toLowerCase()}`; // Use shipper_ prefix for field
    // console.log(`Changing Shipper field: ${fieldKey}, New value: ${value}`);

    setSelectedShipment((prev) => {
      const updatedShipment = {
        ...prev,
        [fieldKey]: value,
      };
      // console.log('Updated selectedShipment state:', updatedShipment);
      return updatedShipment;
    });
  };

  // Function to save shipper field
  const handleSaveShipperField = async (field, value) => {
    const fieldKey = `shipper_${field.toLowerCase()}`; // Ensure this matches the field in your database
    // console.log(`Saving Shipper field: ${fieldKey}, Value: ${value}`);

    // Update the database with the corrected field name (e.g., 'shipper_name' or 'shipper_phone')
    const { data, error } = await supabase
      .from("shipments")
      .update({ [fieldKey]: value })
      .eq("shipment_id", selectedShipment.shipment_id);

    // if (error) {
    //   console.error(`Error updating ${fieldKey} field:`, error);
    // } else {
    //   console.log(`${fieldKey} field updated successfully:`, data);
    // }
  };

  // Function to handle receiver field change
  const handleReceiverFieldChange = (field, value) => {
    const fieldKey = `receiver_${field.toLowerCase()}`; // Use receiver_ prefix for field
    // console.log(`Changing Receiver field: ${fieldKey}, New value: ${value}`);

    setSelectedShipment((prev) => {
      const updatedShipment = {
        ...prev,
        [fieldKey]: value,
      };
      // console.log('Updated selectedShipment state:', updatedShipment);
      return updatedShipment;
    });
  };

  // Function to save receiver field
  const handleSaveReceiverField = async (field, value) => {
    const fieldKey = `receiver_${field.toLowerCase()}`; // Ensure this matches the field in your database
    // console.log(`Saving Receiver field: ${fieldKey}, Value: ${value}`);

    // Update the database with the corrected field name (e.g., 'receiver_name' or 'receiver_phone')
    const { data, error } = await supabase
      .from("shipments")
      .update({ [fieldKey]: value })
      .eq("shipment_id", selectedShipment.shipment_id);

    // if (error) {
    //   console.error(`Error updating ${fieldKey} field:`, error);
    // } else {
    //   console.log(`${fieldKey} field updated successfully:`, data);
    // }
  };
  // =====================End of function for shipper, receiver and shipment fields=================

  // Handle the next page
  const handleNextPage = () => {
    if (currentDocuments.length === documentsPerPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  // ===========================Process for Uploading Documents=======================
  // Confirmation function
  const handleSendDocumentConfirmation = (doc) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="bg-white p-6 rounded-lg shadow-lg relative z-40">
          <button
            onClick={onClose}
            className="absolute top-2 right-2 text-gray-500 text-[30px] hover:text-gray-700"
          >
            &times;
          </button>
          <h2 className="text-lg font-semibold text-blue-500">
            Confirm Send Document
          </h2>
          <p className="mt-2">
            Are you sure you want to send the document titled:{" "}
            {doc.document_title}?
          </p>
          <div className="mt-4">
            <div className="flex justify-between mt-6">
              <button
                onClick={onClose}
                className="bg-gray-300 px-4 py-2 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleSendDocument(doc); // Call actual send function if confirmed
                  onClose(); // Close the confirmation dialog
                }}
                className="bg-blue text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      ),
      overlayClassName: "z-[9998]",
    });
  };

  // Returns true if a document with the same title+url was already sent for the currently viewed shipment
  const isDocumentSent = (doc) => {
    let urls = selectedShipmentForView?.document_urls || [];
    if (typeof urls === "string") {
      try {
        urls = JSON.parse(urls);
      } catch {
        urls = [];
      }
    }
    if (!Array.isArray(urls)) return false;
    return urls.some(
      (item) =>
        String(item?.url || "").trim() ===
          String(doc?.document_url || "").trim() &&
        String(item?.title || "").trim() ===
          String(doc?.document_title || "").trim()
    );
  };

  // Existing handleSendDocument function
  const handleSendDocument = async (doc) => {
    const documentKey = String(
      doc?.id || doc?.document_url || doc?.document_name || ""
    );
    if (sendingDocumentKey && sendingDocumentKey === documentKey) {
      return;
    }

    try {
      setSendingDocumentKey(documentKey);
      // Step 1: Get the current value of the document_urls column
      const { data: currentShipment, error: fetchError } = await supabase
        .from("shipments")
        .select("document_urls")
        .eq("shipment_id", selectedShipmentForView.shipment_id)
        .single();

      if (fetchError) {
        console.error("Error fetching current document URLs:", fetchError);
        throw new Error("Error fetching current document URLs");
      }

      // Step 2: Parse the current document_urls if necessary
      let updatedDocumentUrls = currentShipment.document_urls || [];
      if (typeof updatedDocumentUrls === "string") {
        updatedDocumentUrls = JSON.parse(updatedDocumentUrls);
      }

      const documentAlreadySent = Array.isArray(updatedDocumentUrls)
        ? updatedDocumentUrls.some(
            (item) =>
              String(item?.url || "").trim() ===
                String(doc?.document_url || "").trim() &&
              String(item?.title || "").trim() ===
                String(doc?.document_title || "").trim()
          )
        : false;

      if (documentAlreadySent) {
        toast.info("This document was already sent for this shipment.");
        return;
      }

      // Step 3: Append the new document object (title and URL) to the existing array
      updatedDocumentUrls.push({
        title: doc.document_title,
        url: doc.document_url,
      });

      // Step 4: Update the shipments table with the new array
      const { error: shipmentError } = await supabase
        .from("shipments")
        .update({ document_urls: updatedDocumentUrls })
        .eq("shipment_id", selectedShipmentForView.shipment_id);

      if (shipmentError) {
        console.error("Shipment update error:", shipmentError);
        throw new Error(
          "Error updating shipments table: " + shipmentError.message
        );
      }

      setShipments((prevShipments) =>
        prevShipments.map((shipment) =>
          shipment.shipment_id === selectedShipmentForView.shipment_id
            ? { ...shipment, document_urls: updatedDocumentUrls }
            : shipment
        )
      );
      setSelectedShipmentForView((prev) =>
        prev ? { ...prev, document_urls: updatedDocumentUrls } : prev
      );

      // Step 5: Show success notification
      toast.success("Document sent and shipment updated successfully!");
    } catch (error) {
      console.error("Error sending document:", error);
      toast.error("Failed to send document.");
    } finally {
      setSendingDocumentKey(null);
    }
  };

  // Confirmation function for unsending a document
  const handleUnsendDocumentConfirmation = (doc) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="bg-white p-6 rounded-lg shadow-lg relative z-40">
          <button
            onClick={onClose}
            className="absolute top-2 right-2 text-gray-500 text-[30px] hover:text-gray-700"
          >
            &times;
          </button>
          <h2 className="text-lg font-semibold text-blue-500">
            Confirm Unsend Document
          </h2>
          <p className="mt-2">
            Are you sure you want to unsend the document titled:{" "}
            {doc.document_title}? The client will no longer see it as sent.
          </p>
          <div className="mt-4">
            <div className="flex justify-between mt-6">
              <button
                onClick={onClose}
                className="bg-gray-300 px-4 py-2 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleUnsendDocument(doc);
                  onClose();
                }}
                className="bg-red text-white px-4 py-2 rounded hover:bg-red-600"
              >
                Unsend
              </button>
            </div>
          </div>
        </div>
      ),
      overlayClassName: "z-[9998]",
    });
  };

  // Removes a document from the shipment's sent document_urls
  const handleUnsendDocument = async (doc) => {
    const documentKey = String(
      doc?.id || doc?.document_url || doc?.document_name || ""
    );
    if (sendingDocumentKey && sendingDocumentKey === documentKey) {
      return;
    }

    try {
      setSendingDocumentKey(documentKey);
      const { data: currentShipment, error: fetchError } = await supabase
        .from("shipments")
        .select("document_urls")
        .eq("shipment_id", selectedShipmentForView.shipment_id)
        .single();

      if (fetchError) {
        console.error("Error fetching current document URLs:", fetchError);
        throw new Error("Error fetching current document URLs");
      }

      let updatedDocumentUrls = currentShipment.document_urls || [];
      if (typeof updatedDocumentUrls === "string") {
        updatedDocumentUrls = JSON.parse(updatedDocumentUrls);
      }

      updatedDocumentUrls = Array.isArray(updatedDocumentUrls)
        ? updatedDocumentUrls.filter(
            (item) =>
              !(
                String(item?.url || "").trim() ===
                  String(doc?.document_url || "").trim() &&
                String(item?.title || "").trim() ===
                  String(doc?.document_title || "").trim()
              )
          )
        : [];

      const { error: shipmentError } = await supabase
        .from("shipments")
        .update({ document_urls: updatedDocumentUrls })
        .eq("shipment_id", selectedShipmentForView.shipment_id);

      if (shipmentError) {
        console.error("Shipment update error:", shipmentError);
        throw new Error(
          "Error updating shipments table: " + shipmentError.message
        );
      }

      setShipments((prevShipments) =>
        prevShipments.map((shipment) =>
          shipment.shipment_id === selectedShipmentForView.shipment_id
            ? { ...shipment, document_urls: updatedDocumentUrls }
            : shipment
        )
      );
      setSelectedShipmentForView((prev) =>
        prev ? { ...prev, document_urls: updatedDocumentUrls } : prev
      );

      toast.success("Document unsent successfully!");
    } catch (error) {
      console.error("Error unsending document:", error);
      toast.error("Failed to unsend document.");
    } finally {
      setSendingDocumentKey(null);
    }
  };
  const handleDeleteDocument = async (doc) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="bg-white p-6 rounded-lg shadow-lg relative">
          <button
            onClick={onClose}
            className="absolute top-2 right-2 text-gray-500 text-[30px] hover:text-gray-700"
          >
            &times;
          </button>
          <h2 className="text-lg font-semibold text-blue">Confirm Delete</h2>
          <p className="mt-2">
            Are you sure you want to delete the document:{" "}
            <span className="font-semibold">{doc.document_name}</span>?
          </p>
          <div className="mt-4 flex justify-between">
            <button onClick={onClose} className="bg-gray-300 px-4 py-2 rounded">
              Cancel
            </button>
            <button
              onClick={async () => {
                try {
                  const { error } = await supabase
                    .from("documents")
                    .delete()
                    .eq("document_url", doc.document_url)
                    .eq("shipment_id", selectedShipmentForView.shipment_id);

                  if (error) {
                    console.error("Error deleting document:", error);
                    toast.error("Failed to delete document.");
                    return;
                  }

                  setDocuments((prevDocuments) =>
                    prevDocuments.filter(
                      (d) => d.document_url !== doc.document_url
                    )
                  );

                  // If the deleted document had been sent to the client, unsend it too
                  if (isDocumentSent(doc)) {
                    const { data: currentShipment, error: fetchError } =
                      await supabase
                        .from("shipments")
                        .select("document_urls")
                        .eq(
                          "shipment_id",
                          selectedShipmentForView.shipment_id
                        )
                        .single();

                    if (!fetchError) {
                      let existingUrls = currentShipment.document_urls || [];
                      if (typeof existingUrls === "string") {
                        existingUrls = JSON.parse(existingUrls);
                      }
                      const updatedDocumentUrls = Array.isArray(existingUrls)
                        ? existingUrls.filter(
                            (item) =>
                              !(
                                String(item?.url || "").trim() ===
                                  String(doc?.document_url || "").trim() &&
                                String(item?.title || "").trim() ===
                                  String(doc?.document_title || "").trim()
                              )
                          )
                        : [];

                      const { error: shipmentError } = await supabase
                        .from("shipments")
                        .update({ document_urls: updatedDocumentUrls })
                        .eq(
                          "shipment_id",
                          selectedShipmentForView.shipment_id
                        );

                      if (!shipmentError) {
                        setShipments((prevShipments) =>
                          prevShipments.map((shipment) =>
                            shipment.shipment_id ===
                            selectedShipmentForView.shipment_id
                              ? { ...shipment, document_urls: updatedDocumentUrls }
                              : shipment
                          )
                        );
                        setSelectedShipmentForView((prev) =>
                          prev
                            ? { ...prev, document_urls: updatedDocumentUrls }
                            : prev
                        );
                      }
                    }
                  }

                  toast.success("Document deleted successfully");
                  onClose();
                } catch (error) {
                  console.error("Error deleting document:", error);
                  toast.error("An error occurred while deleting the document.");
                }
              }}
              className="px-4 py-2 rounded bg-red text-white"
            >
              Delete
            </button>
          </div>
        </div>
      ),
      overlayClassName: "!z-[99999]",
    });
  };
  // Handle the previous page
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  const openViewDocumentsModal = async (shipment) => {
    setSelectedShipmentForView(shipment);
    setActiveDocumentSection("all");
    setCurrentPage(1);
    setViewModalOpen(true);

    // Fetch documents related to the selected shipment
    const { data, error } = await supabase
      .from("documents")
      .select("*")
      .eq("shipment_id", shipment.shipment_id); // Filter documents by shipment_id

    if (error) {
      console.error("Error fetching documents:", error.message);
    } else {
      setDocuments(data); // Set the fetched documents
    }
  };

  const openAllDocumentPreviews = () => {
    openDocumentGallery(filteredDocuments, `Documents - ${selectedShipmentForView?.tracking_no || "Shipment"}`);
  };

  // Function to close the view modal
  const closeViewModal = () => {
    setViewModalOpen(false);
  };
  const openModal = (shipment) => {
    setSelectedShipment(shipment);
    setEditableStatus(shipment.status);
    setRemarks("");
    setNewRemarkInputMode("predefined");
    setNewSelectedRemarkOption("");
    setAdditionalRemarkInfo("");
    setRemarkDate("");
    setManualRemarkText("");
    setRemarkFootnote("");
  };

  const closeModal = () => {
    setSelectedShipment(null); // Reset selected shipment
    setEditableStatus(""); // Reset editable status
    setRemarks(""); // Reset remarks to empty
    setNewRemarkInputMode("predefined");
    setNewSelectedRemarkOption("");
    setAdditionalRemarkInfo("");
    setRemarkDate("");
    setManualRemarkText("");
    setRemarkFootnote("");
  };

  const openUploadModal = (shipment) => {
    setSelectedShipmentForUpload(shipment);
    setUploadModalOpen(true);
  };

  const closeUploadModal = () => {
    setUploadModalOpen(false);
    setSelectedShipmentForUpload(null);
    setFile(null); // Reset file input
    setDocumentTitle("");
    setDocumentSection("");
  };
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };
  const handleUpload = async () => {
    if (!file) return toast.error("Please select a file!");
    if (!documentTitle) return toast.error("Please enter a document title!");
    if (!documentSection) return toast.error("Please select a document section!");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("shipment_id", selectedShipmentForUpload.shipment_id);
    formData.append("uploaded_by_user_id", user.user_id || user.id);
    formData.append("uploaded_by_name", user.user_name);
    formData.append("document_title", documentTitle);
    formData.append("document_section", documentSection);

    setLoading2(true); // Start the spinner

    try {
      await api.post(`${API_BASE_URL}/api/upload/upload`, formData, {
        headers: { "Content-Type": undefined },
      });

      toast.success("File uploaded successfully!");
      closeUploadModal(); // Close the modal after upload
    } catch (error) {
      const message =
        error.response?.data?.error || error.message || "Upload failed";
      toast.error(`Error: ${message}`);
    } finally {
      setLoading2(false); // Stop the spinner after upload completes
      closeUploadModal(); // Close the modal after upload
    }
  };
  // =========================End of Process for Uploading Documents========================

  // =========================Process for Fetching, Updating, Transferring, Filtering of Shipments========================
  const handleTransferChange = async () => {
    try {
      const { data } = await api.put(
        `${API_BASE_URL}/api/admin/update-shipment`,
        {
          shipment_id: selectedShipment.shipment_id,
          new_user_id: editableTranserTo,
          job_order_details: selectedShipment.job_order,
        },
        { withCredentials: true }
      );
      setEditableTransferTo(null);
      setjobOrder(null);
    } catch (error) {
      console.log(error);
    }
  };
  const fetchEmployeesData = async () => {
    try {
      const { data } = await api.get(
        `${API_BASE_URL}/api/admin/employees`,
        { withCredentials: true }
      );
      setFetchEmployees(data.employees);
      // console.log(data.employees);
    } catch (error) {
      console.log("Error fetching employees", error);
    }
  };
  const filteredData = shipments.filter((shipment) => {
    // Filter based on search term
    const matchesSearchTerm =
      String(shipment.job_order)
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      String(shipment.shipment_id)
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      String(shipment.created_by)
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      String(shipment.tracking_no)
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      String(shipment.shipper_name)
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      String(shipment.receiver_name)
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      String(shipment.origin)
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      String(shipment.corporate_client)
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      String(shipment.destination)
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      String(shipment.status).toLowerCase().includes(searchTerm.toLowerCase());

    // Corporate filtering logic
    const isCorporate = user?.role === "Corporate";

    let matchesCorporateClientFilter;
    if (isCorporate && department) {
      // Filter by corporate client if user is corporate and has a department
      matchesCorporateClientFilter =
        shipment.corporate_client === user?.department;
    } else {
      // Don't filter by corporate client for non-corporate users
      matchesCorporateClientFilter = true;
    }

    // Filter based on toggle (Owned/Everyone)
    const matchesFilter =
      filter === "Everyone" || String(shipment.user_id) === String(userId);

    const matchesDate = matchesDashboardDateFilter(
      shipment.job_order,
      shipment.date,
      selectedYear,
      selectedMonth
    );
    const matchesShipmentType = selectedShipmentType === "all" || shipment.shipment_type === selectedShipmentType;

    // Match final status condition
    const matchesStatus = matchesShipmentStatusFilter(shipment, "upcoming");

    // // Debug logs (remove these once working)
    // console.log("Shipment:", shipment.shipment_id);
    // console.log("Status:", shipment.status, "Matches Status:", matchesStatus);
    // console.log("Corporate Client:", shipment.corporate_client, "Department:", user?.department);
    // console.log("Matches Corporate Filter:", matchesCorporateClientFilter);
    // console.log("User Role:", user?.role, "Is Corporate:", isCorporate);
    // console.log("User Department:", user?.department); // Add this to see actual department
    // console.log("---");

    return (
      matchesSearchTerm &&
      matchesCorporateClientFilter &&
      matchesFilter &&
      matchesStatus &&
      matchesDate &&
      matchesShipmentType
    );
  });

  useEffect(() => {
    setShipmentsPage(1);
  }, [searchTerm, filter, department, selectedYear, selectedMonth, selectedShipmentType]);

  useEffect(() => {
    const maxPage = Math.max(
      1,
      Math.ceil(filteredData.length / SHIPMENTS_PER_PAGE) || 1
    );
    if (shipmentsPage > maxPage) {
      setShipmentsPage(maxPage);
    }
  }, [filteredData.length, shipmentsPage]);

  const totalShipmentPages = Math.max(
    1,
    Math.ceil(filteredData.length / SHIPMENTS_PER_PAGE) || 1
  );
  const paginatedShipments = filteredData.slice(
    (shipmentsPage - 1) * SHIPMENTS_PER_PAGE,
    shipmentsPage * SHIPMENTS_PER_PAGE
  );
  const shipmentsRangeStart =
    filteredData.length === 0
      ? 0
      : (shipmentsPage - 1) * SHIPMENTS_PER_PAGE + 1;
  const shipmentsRangeEnd = Math.min(
    filteredData.length,
    shipmentsPage * SHIPMENTS_PER_PAGE
  );
  const handleShipmentsPageChange = (newPage) => {
    if (newPage < 1 || newPage > totalShipmentPages) return;
    setShipmentsPage(newPage);
  };

  // The function triggered when the "Save Status" button is clicked
  const handleStatusChange = async () => {
    if (selectedShipment) {
      const departureDate = actualDepartureDate || null;
      const arrivalDate = actualArrivalDate || null;
      const transitTypeValue = transitType || "N/A";
      const transitDateValue = transitDate || "N/A"; // Already in ISO format from the input
      const receivedDateValue = receivedDate || "N/A"; // Already in ISO format from the input

      // Save data to database and wait for completion
      const saved = await saveShipmentData(
        selectedShipment,
        departureDate,
        arrivalDate,
        transitDateValue,
        transitTypeValue,
        receivedDateValue // Always pass the receivedDate
      );
      if (!saved) {
        toast.error("Unable to save shipment status. Please try again.");
        return;
      }
      // Reset fields after saving
      setRemarks("");
      setNewRemarkInputMode("predefined");
      setNewSelectedRemarkOption("");
      setAdditionalRemarkInfo("");
      setRemarkDate("");
      setRemarkFootnote("");
      setWayBill("");
      setActualDepartureDate("");
      setTransitType("");
      setTransitDate(""); // Reset input for transit date
      setActualArrivalDate("");
      setReceivedDate(""); // Reset receivedDate
    }
  };

  const saveShipmentData = async (
    shipment,
    actualDepartureDate,
    actualArrivalDate,
    transitDate,
    transitType,
    receivedDate // Ensure receivedDate is passed here
  ) => {
    const newCurrentStep = getShipmentProgressStepIndex(
      shipment,
      editableStatus
    );
    const timestamp = new Date().toISOString();

    // Retrieve existing remarks from the database
    const { data: existingData, error: fetchError } = await supabase
      .from("shipments")
      .select("remarks")
      .eq("shipment_id", shipment.shipment_id);

    if (fetchError) {
      console.error("Error fetching existing remarks:", fetchError);
      return false;
    }

    const existingRemarks = existingData[0]?.remarks || [];

    // Get the last remark if it exists
    const lastRemark = existingRemarks[existingRemarks.length - 1] || {};

    // Validate and convert transitDate and receivedDate
    const isValidDate = (date) => {
      const parsedDate = new Date(date);
      return !isNaN(parsedDate.getTime());
    };

    const resolvedTransitDate = isValidDate(transitDate)
      ? new Date(transitDate).toISOString()
      : null;

    const resolvedReceivedDate = isValidDate(receivedDate)
      ? new Date(receivedDate).toISOString()
      : lastRemark.receivedDate || null; // Retain the previous receivedDate if not provided

    // Ensure atd and aad are not overwritten with null if already set
    const newAtd = actualDepartureDate || lastRemark.atd || null;
    const newAad = actualArrivalDate || lastRemark.aad || null;

    const newRemarkObject = {
      date: timestamp,
      status: editableStatus,
      remark: remarks || `Updated to ${transitType}`,
      footnote: remarkFootnote || null,
      transitType: transitType || "N/A",
      transitDate: resolvedTransitDate, // Ensure transitDate is included
      receivedDate: resolvedReceivedDate, // Retain or include receivedDate
      atd: newAtd,
      aad: newAad,
    };

    const updatedRemarks = [...existingRemarks, newRemarkObject];

    // Update database
    const { data: updatedShipment, error: updateError } = await supabase
      .from("shipments")
      .update({
        status: editableStatus,
        remarks: updatedRemarks,
        currentstep: newCurrentStep,
        status_updated_at: timestamp,
      })
      .eq("shipment_id", shipment.shipment_id)
      .select("*")
      .single();

    if (updateError || !updatedShipment) {
      console.error("Error updating shipment:", updateError);
      return false;
    }

    updatedShipment.latestRemark = updatedShipment.remarks?.length
      ? updatedShipment.remarks[updatedShipment.remarks.length - 1].remark
      : null;
    setShipments((previousShipments) =>
      previousShipments.map((row) =>
        row.shipment_id === updatedShipment.shipment_id ? updatedShipment : row
      )
    );
    setSelectedShipment((previousShipment) =>
      previousShipment?.shipment_id === updatedShipment.shipment_id
        ? updatedShipment
        : previousShipment
    );
    return true;
  };

  async function handleDelete(row) {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="bg-white p-6 rounded-lg shadow-lg relative z-40">
          <button
            onClick={onClose}
            className="absolute top-2 right-2 text-gray-500 text-[30px] hover:text-gray-700"
          >
            &times;
          </button>
          <h2 className="text-lg font-semibold text-blue">Confirm Delete</h2>
          <p className="mt-2">
            Are you sure you want to delete shipment ID: {row.shipment_id}?
          </p>
          <div className="mt-4">
            <div className="flex justify-between mt-6">
              <button
                onClick={onClose}
                className="bg-gray-300 px-4 py-2 rounded"
              >
                Cancel
              </button>
              <button
                // Inside the Delete button's async onClick:
                onClick={async () => {
                  try {
                    const supabaseClient = supabase;

                    // Step 1: Get shipment (user_id)
                    const { data: shipmentData, error: fetchError } =
                      await supabaseClient
                        .from("shipments")
                        .select("user_id, shipment_id")
                        .eq("shipment_id", row.shipment_id)
                        .single();

                    if (fetchError) {
                      console.error("❌ Error fetching shipment:", fetchError);
                      return;
                    }

                    const userId = shipmentData?.user_id;

                    // Step 2: Delete related job_order records
                    const { error: jobOrderDeleteError } = await supabaseClient
                      .from("job_order")
                      .delete()
                      .eq("shipment_id", row.shipment_id);

                    if (jobOrderDeleteError) {
                      console.error(
                        "❌ Error deleting job_order:",
                        jobOrderDeleteError
                      );
                      return;
                    }

                    // Step 3: Delete related shipment_history records
                    const { error: historyDeleteError } = await supabaseClient
                      .from("shipment_history")
                      .delete()
                      .eq("shipment_id", row.shipment_id);

                    if (historyDeleteError) {
                      console.error(
                        "❌ Error deleting shipment_history:",
                        historyDeleteError
                      );
                      return;
                    }

                    // Step 4: Delete the shipment itself
                    const { error: deleteError } = await supabaseClient
                      .from("shipments")
                      .delete()
                      .eq("shipment_id", row.shipment_id);

                    if (deleteError) {
                      console.error("❌ Error deleting shipment:", deleteError);
                      return;
                    }

                    // Step 5: Decrement shipment_count in users table
                    const { data: userData, error: userFetchError } =
                      await supabaseClient
                        .from("users")
                        .select("shipment_count")
                        .eq("user_id", userId)
                        .single();

                    if (userFetchError) {
                      console.error(
                        "❌ Error fetching shipment_count:",
                        userFetchError
                      );
                      return;
                    }

                    const currentCount = userData?.shipment_count ?? 0;
                    const newCount = currentCount > 0 ? currentCount - 1 : 0;

                    const { error: updateError } = await supabaseClient
                      .from("users")
                      .update({ shipment_count: newCount })
                      .eq("user_id", userId);

                    if (updateError) {
                      console.error(
                        "❌ Error updating shipment_count:",
                        updateError
                      );
                      return;
                    }

                    // Step 6: Update UI
                    setShipments((prev) =>
                      prev.filter(
                        (shipment) => shipment.shipment_id !== row.shipment_id
                      )
                    );

                    onClose();
                  } catch (error) {
                    console.error("❌ Unexpected error:", error);
                  }
                }}
                style={{ backgroundColor: "#dc3545", color: "white" }}
                className="px-4 py-2 rounded"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ),
      overlayClassName: "z-[9998]",
    });
  }
  // =========================Process for Fetching, Updating, Transferring, Filtering of Shipments========================

  // =========================Process of Edit and Delete Remarks========================

  const [editIndex, setEditIndex] = useState(null);
  const [tempRemark, setTempRemark] = useState(""); // Temporary state for remark updates
  const handleEditClick = (index) => {
    setEditIndex(index);
    setTempRemark(selectedShipment.remarks[index]?.remark || ""); // Load the existing remark
  };

  const handleCancelShipment = async (row) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="bg-white p-6 rounded-lg shadow-lg relative z-40 max-w-md">
          <button
            onClick={onClose}
            className="absolute top-2 right-2 text-gray-500 text-[30px] hover:text-gray-700"
          >
            &times;
          </button>
          <h2 className="text-lg font-semibold text-blue">Cancel Shipment</h2>
          <p className="mt-2 text-sm text-gray-700">
            This will mark shipment{" "}
            <span className="font-semibold">{row.tracking_no}</span> as
            cancelled and remove it from shipment nomination.
          </p>
          <div className="mt-4 flex justify-between gap-3">
            <button
              onClick={onClose}
              className="bg-gray-300 px-4 py-2 rounded"
            >
              Keep Shipment
            </button>
            <button
              onClick={async () => {
                try {
                  const nextRemarks = [
                    ...(Array.isArray(row.remarks) ? row.remarks : []),
                    {
                      date: new Date().toISOString(),
                      status: "Cancelled",
                      remark: "Shipment cancelled",
                    },
                  ];

                  await api.put(
                    `${API_BASE_URL}/api/shipment/update-status`,
                    {
                      shipment_id: row.shipment_id,
                      status: "Cancelled",
                      remarks: nextRemarks,
                      actor_user_id: userId,
                      actor_name:
                        user?.user_name ||
                        user?.name ||
                        user?.username ||
                        row.current_user_name,
                    },
                    { withCredentials: true }
                  );

                  toast.success("Shipment cancelled successfully.");
                  onClose();
                } catch (error) {
                  console.error("Error cancelling shipment:", error);
                  toast.error("Failed to cancel shipment.");
                }
              }}
              className="bg-red text-white px-4 py-2 rounded hover:bg-[#af202e]"
            >
              Confirm Cancel
            </button>
          </div>
        </div>
      ),
      overlayClassName: "!z-[99999]",
    });
  };

  const handleInputChangeRemarks = (value) => {
    setTempRemark(value);
  };

  // ✅ Function to update a remark in Supabase
  const handleSaveClick = async () => {
    if (editIndex === null || !selectedShipment) return;

    const updatedRemarks = selectedShipment.remarks.map((item, i) =>
      i === editIndex ? { ...item, remark: tempRemark } : item
    );

    // console.log("Updating remarks in Supabase:", updatedRemarks); // Debug log

    const { data, error } = await supabase
      .from("shipments") // Replace with your actual table name
      .update({ remarks: updatedRemarks }) // Ensure this is an array, not a string
      .eq("shipment_id", selectedShipment.shipment_id);

    if (error) {
      console.error("❌ Failed to update remark:", error.message);
      return;
    }

    // console.log("✅ Update success:", data);

    setSelectedShipment((prev) => ({ ...prev, remarks: updatedRemarks }));
    setEditIndex(null); // Exit edit mode
  };

  // ✅ Function to delete a remark in Supabase
  const handleDeleteRemark = async (index) => {
    if (!selectedShipment) return;

    const updatedRemarks = selectedShipment.remarks.filter(
      (_, i) => i !== index
    );

    // console.log("Deleting remark, new remarks:", updatedRemarks); // Debug log

    const { data, error } = await supabase
      .from("shipments")
      .update({ remarks: updatedRemarks }) // Ensure this is an array, not a string
      .eq("shipment_id", selectedShipment.shipment_id);

    if (error) {
      console.error("❌ Failed to delete remark:", error.message);
      return;
    }

    // console.log("✅ Delete success:", data);

    setSelectedShipment((prev) => ({ ...prev, remarks: updatedRemarks }));
  };
  // =========================End of Edit and Delete Remarks========================
  // =========================Process of Edit and Delete Brokerage Remarks========================

  const [editBrokerageIndex, setEditBrokerageIndex] = useState(null);
  const [tempBrokerageRemark, setTempBrokerageRemark] = useState("");

  // Trigger edit mode with existing input value
  const handleEditBrokerageClick = (index) => {
    setEditBrokerageIndex(index);
    setTempBrokerageRemark(
      selectedShipment.brokerage_remarks[index]?.brokerage_input || ""
    );
  };

  // Handle textarea changes
  const handleInputChangeBrokerage = (value) => {
    setTempBrokerageRemark(value);
  };

  // ✅ Save edited brokerage remark
  const handleSaveBrokerageClick = async () => {
    if (editBrokerageIndex === null || !selectedShipment) return;

    const updatedBrokerageRemarks = selectedShipment.brokerage_remarks.map(
      (item, i) =>
        i === editBrokerageIndex
          ? { ...item, brokerage_input: tempBrokerageRemark }
          : item
    );

    const { data, error } = await supabase
      .from("shipments")
      .update({ brokerage_remarks: updatedBrokerageRemarks })
      .eq("shipment_id", selectedShipment.shipment_id);

    if (error) {
      console.error("❌ Failed to update brokerage remark:", error.message);
      return;
    }

    setSelectedShipment((prev) => ({
      ...prev,
      brokerage_remarks: updatedBrokerageRemarks,
    }));
    setEditBrokerageIndex(null); // Exit edit mode
  };

  // ✅ Delete brokerage remark
  const handleDeleteBrokerageRemark = async (index) => {
    if (!selectedShipment) return;

    const updatedBrokerageRemarks = selectedShipment.brokerage_remarks.filter(
      (_, i) => i !== index
    );

    const { data, error } = await supabase
      .from("shipments")
      .update({ brokerage_remarks: updatedBrokerageRemarks })
      .eq("shipment_id", selectedShipment.shipment_id);

    if (error) {
      console.error("❌ Failed to delete brokerage remark:", error.message);
      return;
    }

    setSelectedShipment((prev) => ({
      ...prev,
      brokerage_remarks: updatedBrokerageRemarks,
    }));
  };

  // ========================Status Helper==========================
  const getFilteredStatuses = (deliveryType, shipmentType) => {
    return getEditableShipmentStatuses({
      ...selectedShipment,
      delivery_mode: deliveryType ?? selectedShipment?.delivery_mode,
      shipment_type: shipmentType ?? selectedShipment?.shipment_type,
    });
  };

  return (
    <div className="shipment-table-container">
      {/* MODERNIZED HEADER SECTION - STACKED LAYOUT */}
      <div className="relative border-b border-blue/10 p-4 mb-6">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-10 w-32 h-32 bg-blue/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-10 w-40 h-40 bg-yellow/5 rounded-full blur-3xl"></div>
        </div>

        <div className="relative space-y-4">
          {/* TOP ROW - Title Section (Left Aligned) */}
          <div className="text-left">
            <h1 className="text-2xl lg:text-3xl font-bold text-blue tracking-tight">
              Shipment Nomination
            </h1>
          </div>

          {/* BOTTOM ROW - Search Bar Section */}
          <div className="flex justify-center">
            <div className="w-full max-w-xl">
              <div className="relative">
                {/* Search input container */}
                <div className="relative">
                  <input
                    type="search"
                    id="shipment-search"
                    className="block w-full px-6 py-3 text-sm font-medium text-gray-900 bg-white/90 backdrop-blur-sm border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 hover:border-gray-300 hover:shadow-lg placeholder-grey/60 shadow-md"
                    placeholder="Search by tracking number, job order, shipper, receiver, status..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                {/* Search results counter */}
                {searchTerm.length > 0 && (
                  <div className="mt-3 text-center">
                    <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue/10 text-blue rounded-full text-sm font-medium">
                      {filteredData.length} shipment
                      {filteredData.length !== 1 ? "s" : ""} found
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[512px] bg-gradient-to-br from-blue/5 to-yellow/5 rounded-2xl">
          <div className="text-center space-y-6 p-8">
            {/* Modern Spinner */}
            <div className="relative">
              {/* Outer ring */}
              <div className="w-16 h-16 border-4 border-blue/20 rounded-full animate-spin border-t-blue mx-auto"></div>
            </div>

            {/* Loading text with animation */}
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-blue animate-pulse">
                Loading Shipments
              </h3>

              <p className="text-sm text-gray-600">
                Fetching shipment nomination data...
              </p>
            </div>

            {/* Optional: Skeleton preview */}
            <div className="hidden lg:block mt-8 space-y-3 opacity-30">
              <div className="flex gap-4">
                <div className="w-20 h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="w-32 h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="w-24 h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="w-28 h-4 bg-gray-200 rounded animate-pulse"></div>
              </div>
              <div className="flex gap-4">
                <div className="w-24 h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="w-20 h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="w-36 h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="w-16 h-4 bg-gray-200 rounded animate-pulse"></div>
              </div>
              <div className="flex gap-4">
                <div className="w-28 h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="w-32 h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="w-20 h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="w-24 h-4 bg-gray-200 rounded animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* DESKTOP TABLE - Hidden on mobile */}
          <div className="hidden lg:block w-full h-[32rem] overflow-x-auto border border-gray-200 rounded-lg bg-white shadow-sm">
            <div className="max-w-[1500px] md:min-w-[1000px]">
              <Table className="min-w-full table-auto border-collapse border border-gray-300">
                <Thead>
                  <Tr className="bg-gray-200 text-left sticky top-0 z-10">
                    <Th className="px-8 border-b border-gray-300 text-nowrap"></Th>
                    <Th className="py-3 px-4 text-gray-700 text-left bg-blue/10 border-b border-blue/20">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-barcode text-blue text-sm"></i>
                        <span className="font-semibold text-blue">
                          Waybill Number
                        </span>
                      </div>
                    </Th>
                    <Th className="px-8 border-b border-gray-300 text-nowrap">
                      Master Waybill
                    </Th>
                    <Th className="px-8 border-b border-gray-300 text-nowrap">
                      House Waybill
                    </Th>
                    {user?.role !== "Corporate" && (
                      <Th className="px-8 border-b border-gray-300 text-nowrap">
                        Job Order
                      </Th>
                    )}
                    {user?.role !== "Corporate" && (
                      <Th className="px-8 border-b border-gray-300 text-nowrap">
                        Created By
                      </Th>
                    )}
                    {user?.role !== "Corporate" && (
                      <Th className="px-8 border-b border-gray-300 text-nowrap">
                        Transferred to
                      </Th>
                    )}
                    <Th className="px-8 border-b border-gray-300 text-nowrap">
                      Shipper
                    </Th>
                    <Th className="px-8 border-b border-gray-300 text-nowrap">
                      Receiver
                    </Th>
                    <Th className="px-8 border-b border-gray-300 text-nowrap">
                      Status
                    </Th>
                    {user?.role !== "Corporate" && (
                      <Th className="px-8 border-b border-gray-300 text-nowrap">
                        Delivery Mode
                      </Th>
                    )}
                    {user?.role !== "Corporate" && (
                      <Th className="px-8 border-b border-gray-300 text-nowrap">
                        Corporate Client
                      </Th>
                    )}
                    <Th className="px-8 border-b border-gray-300 text-nowrap">
                      Remarks
                    </Th>
                    <Th className="px-8 border-b border-gray-300 text-nowrap">
                      ETD
                    </Th>
                    <Th className="px-8 border-b border-gray-300 text-nowrap">
                      ETA
                    </Th>
                    {user?.role !== "Corporate" && (
                      <Th className="px-8 border-b border-gray-300 text-nowrap">
                        ATD
                      </Th>
                    )}
                    <Th className="px-8 border-b border-gray-300 text-nowrap">
                      ATA
                    </Th>
                    <Th className="px-6 py-3 border-b border-gray-300 bg-gray-200 text-blue-900 sticky right-0 shadow-md">
                      Action
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredData.length > 0 ? (
                    paginatedShipments.map((shipment, index) => {
                      const isDeliveredOrTurnover =
                        shipment.status === "In Warehouse" ||
                        shipment.status === "Departure at Origin" ||
                        shipment.status === "In Transit" ||
                        shipment.status === "Arrival at Destination" ||
                        shipment.status === "Notification" ||
                        shipment.status === "Custom Clearance";

                      return (
                        <Tr
                          key={shipment.shipment_id}
                          className={`cursor-pointer border-b transition-colors ${
                            activeRow === shipment.shipment_id
                              ? "bg-gray-200"
                              : "hover:bg-gray-50"
                          }`}
                          onClick={() => {
                            setActiveRow(shipment.shipment_id);
                            if (onRowClick) onRowClick(shipment);
                          }}
                        >
                          {/* Status indicator */}
                          <Td className="px-4 border-b border-gray-300 relative text-center">
                            {isDeliveredOrTurnover ? (
                              <div className="group relative inline-block">
                                <i className="fa-solid fa-circle-check text-lg bg-[#90EE90] text-white p-1 rounded-full cursor-pointer"></i>
                                <span className="absolute left-full ml-2 -top-2 -translate-y-1/2 hidden group-hover:flex bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10 shadow-md">
                                  This shipment is completed
                                </span>
                              </div>
                            ) : (
                              <div className="group relative inline-block">
                                <i className="fa-solid fa-calendar text-xl text-green-500 cursor-pointer"></i>
                                <span className="absolute left-full ml-2 -top-2 -translate-y-1/2 hidden group-hover:flex bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10 shadow-md">
                                  This shipment is updated
                                </span>
                              </div>
                            )}
                          </Td>

                          {/* Tracking Number */}
                          <Td
                            className={`py-3 px-4 ${
                              user?.role === "Corporate"
                                ? "bg-gray-50"
                                : getKPIStatus(shipment)?.bgColor ||
                                  "bg-gray-50"
                            } group hover:bg-blue/5 transition-all duration-200 relative`}
                          >
                            <div className="flex items-center gap-3">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(
                                    shipment.tracking_no
                                  );
                                  toast.success(
                                    <div className="flex items-center gap-2">
                                      <span>Copied to clipboard!</span>
                                    </div>,
                                    {
                                      position: "top-right",
                                    }
                                  );
                                }}
                                className="w-8 h-8 bg-blue/10 hover:bg-blue hover:text-white text-blue rounded-xl flex items-center justify-center transition-all duration-300 transform hover:scale-110 shadow-sm hover:shadow-lg flex-shrink-0"
                                title="Copy tracking number"
                              >
                                <i className="fas fa-copy text-xs"></i>
                              </button>
                              <span
                                className="font-mono text-blue font-semibold cursor-pointer hover:text-blue/80 transition-colors duration-200 truncate"
                                title={shipment.tracking_no}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(
                                    shipment.tracking_no
                                  );
                                  toast.success(
                                    <div className="flex items-center gap-2">
                                      <span>Copied to clipboard!</span>
                                    </div>,
                                    {
                                      position: "top-right",
                                    }
                                  );
                                }}
                              >
                                {shipment.tracking_no}
                              </span>
                            </div>
                          </Td>

                          {/* Master / House Waybill */}
                          <Td className="px-8 border-b border-gray-300 truncate">
                            {shipment.master_waybill}
                          </Td>
                          <Td className="px-8 border-b border-gray-300 truncate">
                            {shipment.house_waybill}
                          </Td>

                          {/* Other columns - same as original */}
                          {user?.role !== "Corporate" && (
                            <Td className="px-8 border-b border-gray-300 truncate">
                              {shipment.job_order}
                            </Td>
                          )}
                          {user?.role !== "Corporate" && (
                            <Td className="px-8 border-b border-gray-300 truncate">
                              {shipment.created_by}
                            </Td>
                          )}
                          {user?.role !== "Corporate" && (
                            <Td className="px-8 border-b border-gray-300 truncate">
                              {shipment.current_user_name
                                ? shipment.current_user_name
                                : "-"}
                            </Td>
                          )}
                          <Td className="px-8 border-b border-gray-300 truncate">
                            {shipment.shipper_name}
                          </Td>
                          <Td className="px-8 border-b border-gray-300 truncate">
                            {shipment.receiver_name}
                          </Td>
                          <Td className="py-3 px-4">
                            <span className={getStatusClass(shipment.status, shipment)}>
                              {shipment.status}
                            </span>
                          </Td>
                          {user?.role !== "Corporate" && (
                            <Td className="px-8 border-b border-gray-300 truncate">
                              {shipment.delivery_mode}
                            </Td>
                          )}
                          {user?.role !== "Corporate" && (
                            <Td className="px-8 border-b border-gray-300 truncate">
                              {shipment.corporate_client}
                            </Td>
                          )}
                          <Td className="px-8 border-b border-gray-300 truncate whitespace-pre-line">
                            {[
                              "Order Placed",
                              "In Warehouse",
                              "Departure at Origin",
                              "In Transit",
                              "Arrival at Destination",
                              "Custom Clearance",
                            ].includes(shipment.status) ? (
                              <span className="font-bold text-[12px] text-blue">
                                {shipment.remarks?.length > 0 &&
                                shipment.remarks[shipment.remarks.length - 1]
                                  .remark
                                  ? shipment.remarks[
                                      shipment.remarks.length - 1
                                    ].remark
                                      .split("\n")
                                      .map((line, idx) => (
                                        <React.Fragment key={idx}>
                                          {line}
                                          <br />
                                        </React.Fragment>
                                      ))
                                  : "-"}
                              </span>
                            ) : (
                              "-"
                            )}
                          </Td>
                          {/* ETD */}
                          <Td className="pb-2 sm:px-8 border-b border-gray-300 truncate">
                            {shipment.etd
                              ? new Intl.DateTimeFormat("en-US", {
                                  month: "long",
                                  day: "2-digit",
                                  year: "numeric",
                                  timeZone: "Asia/Manila",
                                }).format(new Date(shipment.etd))
                              : "N/A"}
                          </Td>
                          {/* ETA */}
                          <Td className="pb-2 sm:px-8 border-b border-gray-300 truncate">
                            {shipment.eta
                              ? new Intl.DateTimeFormat("en-US", {
                                  month: "long",
                                  day: "2-digit",
                                  year: "numeric",
                                  timeZone: "Asia/Manila",
                                }).format(new Date(shipment.eta))
                              : "N/A"}
                          </Td>
                          {/* ATD */}
                          {user?.role !== "Corporate" && (
                            <Td className="px-8 border-b border-gray-300 truncate">
                              {shipment.remarks?.length > 0 &&
                              shipment.remarks[shipment.remarks.length - 1].atd
                                ? new Intl.DateTimeFormat("en-US", {
                                    month: "long",
                                    day: "2-digit",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                    timeZone: "Asia/Manila",
                                  }).format(
                                    new Date(
                                      shipment.remarks[
                                        shipment.remarks.length - 1
                                      ].atd
                                    )
                                  )
                                : "-"}
                            </Td>
                          )}
                          {/* ATA */}
                          <Td className="px-8 border-b border-gray-300 truncate">
                            {shipment.remarks?.length > 0 &&
                            shipment.remarks[shipment.remarks.length - 1].aad
                              ? new Intl.DateTimeFormat("en-US", {
                                  month: "long",
                                  day: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                  timeZone: "Asia/Manila",
                                }).format(
                                  new Date(
                                    shipment.remarks[
                                      shipment.remarks.length - 1
                                    ].aad
                                  )
                                )
                              : "-"}
                          </Td>
                          {/* Actions */}
                          <Td className="p-2 border-b border-gray-300 fixed-col min-w-[12rem] bg-[#e5e7eb] sticky right-0 shadow-md">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {user?.role === "Corporate" ? (
                                <>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openDetailsModal(shipment);
                                    }}
                                    className="bg-blue hover:bg-[#07335c] text-white px-4 py-2 text-xs flex rounded-md gap-2 items-center justify-center"
                                  >
                                    <i className="fa-solid fa-eye"></i>
                                    Details
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openHistoryModal(shipment);
                                    }}
                                    className="bg-blue hover:bg-[#07335c] text-white px-4 py-2 text-xs flex rounded-md gap-2 items-center justify-center"
                                  >
                                    <i className="fa-solid fa-clock-rotate-left"></i>
                                    History
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openViewDocumentsModal(shipment);
                                    }}
                                    className="bg-blue hover:bg-[#07335c] text-white px-4 py-2 text-xs flex rounded-md gap-2 items-center justify-center"
                                  >
                                    <i className="fa-solid fa-clock-rotate-left"></i>
                                    Docs
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openModal(shipment);
                                    }}
                                    className="bg-blue hover:bg-[#07335c] text-white px-4 py-2 text-sm flex rounded-md gap-2 items-center justify-center"
                                  >
                                    <i className="fa-solid fa-pen-to-square"></i>
                                    Edit
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openUploadModal(shipment);
                                    }}
                                    className="bg-blue hover:bg-[#07335c] text-white px-4 py-2 text-sm flex rounded-md gap-2 items-center justify-center"
                                  >
                                    <i className="fa-solid fa-upload"></i>Upload
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openViewDocumentsModal(shipment);
                                    }}
                                    className="bg-blue hover:bg-[#07335c] text-white px-4 py-2 text-sm flex rounded-md gap-2 items-center justify-center"
                                  >
                                    <i className="fa-solid fa-file-alt"></i>Docs
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCancelShipment(shipment);
                                    }}
                                    className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 text-sm flex rounded-md gap-2 items-center justify-center"
                                  >
                                    <i className="fa-solid fa-ban"></i>Cancel
                                  </button>
                                  <button
                                    onClick={() => handleDelete(shipment)}
                                    className="bg-red hover:bg-[#af202e] text-white px-4 py-2 text-sm flex rounded-md gap-2 items-center justify-center"
                                  >
                                    <i className="fa-solid fa-trash"></i>Delete
                                  </button>
                                </>
                              )}
                            </div>
                          </Td>
                        </Tr>
                      );
                    })
                  ) : (
                    <Tr>
                      <Td
                        colSpan="12"
                        className="text-center py-4 text-gray-500"
                      >
                        No shipments available.
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </div>
          </div>

          {/* MOBILE CARDS - Visible only on mobile */}
          <div className="lg:hidden space-y-4">
            {filteredData.length > 0 ? (
              paginatedShipments.map((shipment, index) => {
                const isDeliveredOrTurnover =
                  shipment.status === "In Warehouse" ||
                  shipment.status === "Departure at Origin" ||
                  shipment.status === "In Transit" ||
                  shipment.status === "Arrival at Destination" ||
                  shipment.status === "Notification" ||
                  shipment.status === "Custom Clearance";

                return (
                  <div
                    key={shipment.shipment_id}
                    className={`bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200 p-4 transition-all duration-300 hover:shadow-xl hover:border-blue/30 cursor-pointer ${
                      activeRow === shipment.shipment_id
                        ? "ring-2 ring-blue/30 bg-blue/5"
                        : ""
                    }`}
                    onClick={() => {
                      setActiveRow(shipment.shipment_id);
                      if (onRowClick) onRowClick(shipment);
                    }}
                  >
                    {/* ROW 1: Status Indicator */}
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex-shrink-0">
                        {isDeliveredOrTurnover ? (
                          <i className="fa-solid fa-circle-check text-lg bg-green-500 text-white p-1.5 rounded-full"></i>
                        ) : (
                          <i className="fa-solid fa-calendar text-lg text-green-500 p-1"></i>
                        )}
                      </div>
                      <span className="text-sm text-gray-600 font-medium">
                        {isDeliveredOrTurnover
                          ? "Shipment Active"
                          : "Shipment Updated"}
                      </span>
                    </div>

                    {/* ROW 2: Tracking Number with Copy - FIXED OVERFLOW */}
                    <div
                      className={`${
                        user?.role === "Corporate"
                          ? "bg-blue/10"
                          : getKPIStatus(shipment)?.bgColor || "bg-blue/10"
                      } rounded-xl p-3 mb-4`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <i className="fas fa-barcode text-blue text-sm"></i>
                        <span className="text-xs font-semibold text-gray-600">
                          Waybill Number
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(shipment.tracking_no);
                            toast.success(
                              <div className="flex items-center gap-2">
                                <span>Copied to clipboard!</span>
                              </div>,
                              {
                                position: "top-right",
                              }
                            );
                          }}
                          className="w-8 h-8 bg-blue/20 hover:bg-blue hover:text-white text-blue rounded-xl flex items-center justify-center transition-all duration-300 flex-shrink-0"
                        >
                          <i className="fas fa-copy text-xs"></i>
                        </button>
                        {/* FIXED: Added proper text wrapping and overflow handling */}
                        <span className="font-mono text-blue font-bold text-sm sm:text-base md:text-lg flex-1 break-all overflow-hidden">
                          {shipment.tracking_no}
                        </span>
                      </div>
                    </div>

                    {/* Master / House Waybill */}
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      <div>
                        <span className="text-xs font-semibold text-gray-600 block mb-1">
                          Master Waybill
                        </span>
                        <span className="text-sm text-gray-800 break-all">
                          {shipment.master_waybill || "-"}
                        </span>
                      </div>
                      <div>
                        <span className="text-xs font-semibold text-gray-600 block mb-1">
                          House Waybill
                        </span>
                        <span className="text-sm text-gray-800 break-all">
                          {shipment.house_waybill || "-"}
                        </span>
                      </div>
                    </div>

                    {/* ROW 3: Job Status Badge */}
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="fas fa-flag text-blue text-sm"></i>
                        <span className="text-xs font-semibold text-gray-600">
                          Current Status
                        </span>
                      </div>
                      <div className="flex justify-center">
                        <span
                          className={`${getStatusClass(
                            shipment.status, shipment
                          )} text-sm px-4 py-2 rounded-full font-semibold text-center`}
                        >
                          {shipment.status}
                        </span>
                      </div>
                    </div>

                    {/* ROW 4: Shipper Information - FIXED OVERFLOW */}
                    <div className="bg-gray-50 rounded-xl p-3 mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="fas fa-user-tie text-blue text-sm"></i>
                        <span className="text-xs font-semibold text-gray-600">
                          Shipper Information
                        </span>
                      </div>
                      <p
                        className="text-sm font-bold text-gray-900 break-words overflow-hidden"
                        title={shipment.shipper_name}
                      >
                        {shipment.shipper_name}
                      </p>
                    </div>

                    {/* ROW 5: Receiver Information - FIXED OVERFLOW */}
                    <div className="bg-gray-50 rounded-xl p-3 mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="fas fa-user-check text-blue text-sm"></i>
                        <span className="text-xs font-semibold text-gray-600">
                          Receiver Information
                        </span>
                      </div>
                      <p
                        className="text-sm font-bold text-gray-900 break-words overflow-hidden"
                        title={shipment.receiver_name}
                      >
                        {shipment.receiver_name}
                      </p>
                    </div>

                    {/* ROW 6: Date Information */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="bg-gray-50 rounded-xl p-3 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <i className="fas fa-plane-departure text-blue text-sm"></i>
                          <span className="text-xs font-semibold text-gray-600">
                            ETD
                          </span>
                        </div>
                        <p className="text-sm font-bold text-gray-900 truncate">
                          {shipment.etd
                            ? new Intl.DateTimeFormat("en-US", {
                                month: "short",
                                day: "2-digit",
                                year: "numeric",
                                timeZone: "Asia/Manila",
                              }).format(new Date(shipment.etd))
                            : "N/A"}
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-3 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <i className="fas fa-plane-arrival text-blue text-sm"></i>
                          <span className="text-xs font-semibold text-gray-600">
                            ETA
                          </span>
                        </div>
                        <p className="text-sm font-bold text-gray-900 truncate">
                          {shipment.eta
                            ? new Intl.DateTimeFormat("en-US", {
                                month: "short",
                                day: "2-digit",
                                year: "numeric",
                                timeZone: "Asia/Manila",
                              }).format(new Date(shipment.eta))
                            : "N/A"}
                        </p>
                      </div>
                    </div>

                    {/* ROW 7: Additional info for non-Corporate users - FIXED OVERFLOW */}
                    {user?.role !== "Corporate" && (
                      <>
                        <div className="bg-gray-50 rounded-xl p-3 mb-4">
                          <div className="flex items-center gap-2 mb-2">
                            <i className="fas fa-clipboard-list text-blue text-sm"></i>
                            <span className="text-xs font-semibold text-gray-600">
                              Job Order
                            </span>
                          </div>
                          <p className="text-sm font-bold text-gray-900 break-words">
                            {shipment.job_order}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-3 mb-4">
                          <div className="flex items-center gap-2 mb-2">
                            <i className="fas fa-user text-blue text-sm"></i>
                            <span className="text-xs font-semibold text-gray-600">
                              Created By
                            </span>
                          </div>
                          <p
                            className="text-sm font-bold text-gray-900 break-words overflow-hidden"
                            title={shipment.created_by}
                          >
                            {shipment.created_by}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-3 mb-4">
                          <div className="flex items-center gap-2 mb-2">
                            <i className="fas fa-building text-blue text-sm"></i>
                            <span className="text-xs font-semibold text-gray-600">
                              Corporate Client
                            </span>
                          </div>
                          <p
                            className="text-sm font-bold text-gray-900 break-words overflow-hidden"
                            title={shipment.corporate_client}
                          >
                            {shipment.corporate_client || "—"}
                          </p>
                        </div>
                      </>
                    )}

                    {/* ROW 8: Remarks - FIXED OVERFLOW */}
                    {[
                      "Order Placed",
                      "In Warehouse",
                      "Departure at Origin",
                      "In Transit",
                      "Arrival at Destination",
                      "Custom Clearance",
                    ].includes(shipment.status) && (
                      <div className="bg-gray-50 rounded-xl p-3 mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <i className="fas fa-comment-alt text-blue text-sm"></i>
                          <span className="text-xs font-semibold text-gray-600">
                            Latest Remarks
                          </span>
                        </div>
                        <div className="text-sm text-gray-700 leading-relaxed break-words overflow-hidden">
                          {shipment.remarks?.length > 0 &&
                          shipment.remarks[shipment.remarks.length - 1].remark
                            ? renderVisibleShipmentRemark(
                                shipment.remarks[
                                  shipment.remarks.length - 1
                                ].remark
                              )
                            : "No remarks available"}
                        </div>
                      </div>
                    )}

                    {/* ROW 9: Action Buttons */}
                    <div className="border-t border-gray-200 pt-4">
                      <div className="grid grid-cols-2 gap-2">
                        {user?.role === "Corporate" ? (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openDetailsModal(shipment);
                              }}
                              className="bg-blue hover:bg-blue/90 text-white px-3 py-2 text-sm flex rounded-xl gap-2 items-center justify-center transition-all duration-300 transform hover:scale-[1.02] shadow-md hover:shadow-lg"
                            >
                              <i className="fa-solid fa-eye text-sm"></i>
                              <span>Details</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openHistoryModal(shipment);
                              }}
                              className="bg-blue hover:bg-blue/90 text-white px-3 py-2 text-sm flex rounded-xl gap-2 items-center justify-center transition-all duration-300 transform hover:scale-[1.02] shadow-md hover:shadow-lg"
                            >
                              <i className="fa-solid fa-clock-rotate-left text-sm"></i>
                              <span>History</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openViewDocumentsModal(shipment);
                              }}
                              className="bg-blue hover:bg-blue/90 text-white px-3 py-2 text-sm flex rounded-xl gap-2 items-center justify-center transition-all duration-300 transform hover:scale-[1.02] shadow-md hover:shadow-lg col-span-2"
                            >
                              <i className="fa-solid fa-file-alt text-sm"></i>
                              <span>Documents</span>
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openModal(shipment);
                              }}
                              className="bg-blue hover:bg-blue/90 text-white px-3 py-2 text-sm flex rounded-xl gap-2 items-center justify-center transition-all duration-300 transform hover:scale-[1.02] shadow-md hover:shadow-lg"
                            >
                              <i className="fa-solid fa-pen-to-square text-sm"></i>
                              <span>Edit</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openUploadModal(shipment);
                              }}
                              className="bg-blue hover:bg-blue/90 text-white px-3 py-2 text-sm flex rounded-xl gap-2 items-center justify-center transition-all duration-300 transform hover:scale-[1.02] shadow-md hover:shadow-lg"
                            >
                              <i className="fa-solid fa-upload text-sm"></i>
                              <span>Upload</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openViewDocumentsModal(shipment);
                              }}
                              className="bg-blue hover:bg-blue/90 text-white px-3 py-2 text-sm flex rounded-xl gap-2 items-center justify-center transition-all duration-300 transform hover:scale-[1.02] shadow-md hover:shadow-lg"
                            >
                              <i className="fa-solid fa-file-alt text-sm"></i>
                              <span>Docs</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancelShipment(shipment);
                              }}
                              className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 text-sm flex rounded-xl gap-2 items-center justify-center transition-all duration-300 transform hover:scale-[1.02] shadow-md hover:shadow-lg"
                            >
                              <i className="fa-solid fa-ban text-sm"></i>
                              <span>Cancel</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(shipment);
                              }}
                              className="bg-red hover:bg-red/90 text-white px-3 py-2 text-sm flex rounded-xl gap-2 items-center justify-center transition-all duration-300 transform hover:scale-[1.02] shadow-md hover:shadow-lg"
                            >
                              <i className="fa-solid fa-trash text-sm"></i>
                              <span>Delete</span>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-ship text-gray-400 text-2xl"></i>
                </div>
                <p className="text-gray-500 font-medium">
                  No shipments available.
                </p>
              </div>
            )}
          </div>
        </>
      )}
      {filteredData.length > 0 && (
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm text-gray-600">
            Showing {shipmentsRangeStart}&nbsp;–&nbsp;{shipmentsRangeEnd} of{" "}
            {filteredData.length} shipments
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleShipmentsPageChange(shipmentsPage - 1)}
              disabled={shipmentsPage === 1}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold transition ${
                shipmentsPage === 1
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-blue text-white hover:bg-blue/90"
              }`}
            >
              Previous
            </button>
            <span className="text-sm text-gray-700">
              Page {shipmentsPage} of {totalShipmentPages}
            </span>
            <button
              type="button"
              onClick={() => handleShipmentsPageChange(shipmentsPage + 1)}
              disabled={shipmentsPage === totalShipmentPages}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold transition ${
                shipmentsPage === totalShipmentPages
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-blue text-white hover:bg-blue/90"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
      {selectedShipment && user?.role !== "Corporate" && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white w-[90%] max-w-[1000px] max-h-[65rem] h-[80vh] p-6 rounded-lg shadow-lg relative flex flex-col justify-between gap-4">
            <button
              onClick={closeModal}
              className="absolute top-2 right-2 text-gray-500 text-[30px] hover:text-gray-700"
            >
              &times;
            </button>
            <h2 className="text-lg font-semibold text-blue">
              Edit Shipment Status
            </h2>

            {/* Tab Buttons */}
            <div className="flex flex-wrap gap-4 justify-center">
              {["shipper", "receiver", "shipment"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`w-full sm:w-max px-4 py-2 rounded ${
                    activeTab === tab
                      ? "bg-blue text-white"
                      : "bg-gray-200 text-blue"
                  }`}
                >
                  {`${tab.charAt(0).toUpperCase() + tab.slice(1)} Details`}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="p-4 overflow-y-auto w-full">
              {activeTab === "shipper" && (
                <div className="flex flex-col">
                  {[
                    "Name",
                    "Country",
                    "City",
                    "Building",
                    "Email",
                    "Phone",
                    "Street",
                    "Landmark",
                  ].map((field) => (
                    <div
                      className="flex flex-wrap flex-row justify-between w-full mb-5"
                      key={field}
                    >
                      <span className="text-gray-500">Shipper {field}</span>
                      {editableShipperFields.includes(field) ? (
                        <input
                          type="text"
                          value={
                            selectedShipment[`shipper_${field.toLowerCase()}`]
                          }
                          onChange={(e) =>
                            handleShipperFieldChange(field, e.target.value)
                          }
                          onBlur={() =>
                            handleSaveShipperField(
                              field,
                              selectedShipment[`shipper_${field.toLowerCase()}`]
                            )
                          } // Save on blur
                          className="border border-gray-300 p-1 rounded text-blue w-full"
                        />
                      ) : (
                        <span className="text-blue font-bold">
                          {selectedShipment[`shipper_${field.toLowerCase()}`]}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {activeTab === "receiver" && (
                <div className="flex flex-col">
                  {[
                    "Name",
                    "Country",
                    "City",
                    "Building",
                    "Email",
                    "Phone",
                    "Street",
                    "Landmark",
                  ].map((field) => (
                    <div
                      className="flex flex-wrap flex-row justify-between w-full mb-5"
                      key={field}
                    >
                      <span className="text-gray-500">Receiver {field}</span>
                      {editableReceiverFields.includes(field) ? (
                        <input
                          type="text"
                          value={
                            selectedShipment[`receiver_${field.toLowerCase()}`]
                          }
                          onChange={(e) =>
                            handleReceiverFieldChange(field, e.target.value)
                          }
                          onBlur={() =>
                            handleSaveReceiverField(
                              field,
                              selectedShipment[
                                `receiver_${field.toLowerCase()}`
                              ]
                            )
                          } // Save on blur
                          className="border border-gray-300 p-1 rounded text-blue w-full"
                        />
                      ) : (
                        <span className="text-blue font-bold">
                          {selectedShipment[`receiver_${field.toLowerCase()}`]}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {activeTab === "shipment" && (
                <div className="flex flex-col">
                  {/* Base fields always shown */}
                  {[
                    { label: "Description", key: "shipment_description" },
                    { label: "Type", key: "shipment_type" },
                    { label: "Instructions", key: "shipment_instructions" },
                    { label: "Mode", key: "shipment_mode" },
                    { label: "Delivery", key: "delivery_mode" },
                    {
                      label: "Gross Weight",
                      key: "shipment_grossweight",
                      disabled: selectedShipment.shipment_mode === "SEA",
                    },
                    {
                      label: "Chargeable Weight",
                      key: "shipment_chargeableweight",
                      disabled: selectedShipment.shipment_mode === "SEA",
                    },
                    {
                      label: "Dimensions",
                      key: "shipment_dimensions",
                      disabled: selectedShipment.shipment_mode === "SEA",
                    },
                    { label: "Volume", key: "shipment_volume" },
                    { label: "Packages", key: "shipment_packages" },

                    { label: "ETD", key: "etd", isDateTime: true },
                    { label: "ETA", key: "eta", isDateTime: true },
                  ].map(
                    ({ label, key, type, options, isDateTime, disabled }) => (
                      <div
                        className="flex flex-wrap flex-row justify-between w-full mb-5"
                        key={key}
                      >
                        <span className="text-gray-500">Shipment {label}</span>
                        {type === "dropdown" ? (
                          <select
                            value={selectedShipment[key] || ""}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              handleFieldChange("shipment", key, newValue);
                              handleSaveField("shipment", key, newValue);
                            }}
                            className="border border-gray-300 p-1 rounded text-blue w-full"
                            disabled={disabled}
                          >
                            <option value="" disabled>
                              Select {label}
                            </option>
                            {options.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type={isDateTime ? "datetime-local" : "text"}
                            value={
                              isDateTime
                                ? selectedShipment[key]
                                  ? new Date(selectedShipment[key])
                                      .toLocaleString("sv-SE", {
                                        timeZone: "Asia/Manila",
                                        hour12: false,
                                      })
                                      .replace(" ", "T") // Convert to 'YYYY-MM-DDTHH:mm'
                                  : ""
                                : selectedShipment[key] || ""
                            }
                            onChange={(e) => {
                              const newValue = isDateTime
                                ? new Date(e.target.value).toISOString() // Save as UTC in ISO format
                                : e.target.value;

                              // Update local state and save to database
                              handleFieldChange("shipment", key, newValue);
                              handleSaveField("shipment", key, newValue);
                            }}
                            className="border border-gray-300 p-1 rounded text-blue w-full"
                            disabled={disabled}
                          />
                        )}
                      </div>
                    )
                  )}
                  <div className="flex flex-wrap flex-row justify-between w-full mb-5">
                    <span className="text-gray-500">Corporate Client</span>
                    <div className="w-full">
                      <select
                        value={selectedShipment.corporate_client || ""}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          handleFieldChange(
                            "shipment",
                            "corporate_client",
                            newValue
                          );
                          handleSaveField(
                            "shipment",
                            "corporate_client",
                            newValue
                          );
                        }}
                        className="border border-gray-300 p-1 rounded text-blue w-full"
                      >
                        <option value="">No Corporate Client</option>
                        {corporateClients.map((client) => (
                          <option key={client} value={client}>
                            {client}
                          </option>
                        ))}
                      </select>
                      {selectedShipment.corporate_client ? (
                        <button
                          type="button"
                          onClick={() => {
                            handleFieldChange("shipment", "corporate_client", "");
                            handleSaveField("shipment", "corporate_client", "");
                          }}
                          className="mt-2 text-xs font-semibold text-red-600 hover:text-red-800"
                        >
                          Remove selected client
                        </button>
                      ) : null}
                    </div>
                  </div>
                  {(selectedShipment?.shipment_type === "SEA" ||
                    selectedShipment?.shipment_type === "AIR") && (
                    <div className="flex flex-wrap flex-row justify-between w-full mb-5">
                      <span className="text-gray-500">Delivery Mode</span>
                      <select
                        value={selectedShipment?.delivery_mode || ""}
                        onChange={(e) =>
                          setSelectedShipment({
                            ...selectedShipment,
                            delivery_mode: e.target.value,
                          })
                        }
                        className="border border-gray-300 p-1 rounded text-blue w-full w-[50%] font-bold"
                      >
                        <option className="text-gray-500" value="">
                          Select Delivery Mode
                        </option>
                        {[
                          "DOOR-DOOR",
                          "DOOR-PORT",
                          "PORT-PORT",
                          "PORT-DOOR",
                        ].map((mode) => (
                          <option key={mode} value={mode}>
                            {mode}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {/* Conditional fields for non-AIR mode */}
                  {selectedShipment.shipment_mode !== "AIR" && (
                    <>
                      <div className="flex flex-wrap flex-row justify-between w-full mb-5">
                        <span className="text-gray-500">Shipment Terms</span>
                        <select
                          value={selectedShipment.shipment_terms || ""}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            handleFieldChange(
                              "shipment",
                              "shipment_terms",
                              newValue
                            );
                            handleSaveField(
                              "shipment",
                              "shipment_terms",
                              newValue
                            );
                          }}
                          className="border border-gray-300 p-1 rounded text-blue w-full"
                        >
                          <option value="" disabled>
                            Select Terms
                          </option>
                          <option value="LCL">LCL</option>
                          <option value="FCL">FCL</option>
                        </select>
                      </div>
                      {selectedShipment.shipment_terms === "FCL" && (
                        <>
                          <div className="flex flex-wrap flex-row justify-between w-full mb-5">
                            <span className="text-gray-500">Containers</span>
                            <input
                              type="text"
                              value={selectedShipment.shipment_containers || ""}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                handleFieldChange(
                                  "shipment",
                                  "shipment_containers",
                                  newValue
                                );
                                handleSaveField(
                                  "shipment",
                                  "shipment_containers",
                                  newValue
                                );
                              }}
                              className="border border-gray-300 p-1 rounded text-blue w-full"
                            />
                          </div>
                          <div className="flex flex-wrap flex-row justify-between w-full mb-5">
                            <span className="text-gray-500">Weight</span>
                            <input
                              type="text"
                              value={selectedShipment.shipment_weight || ""}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                handleFieldChange(
                                  "shipment",
                                  "shipment_weight",
                                  newValue
                                );
                                handleSaveField(
                                  "shipment",
                                  "shipment_weight",
                                  newValue
                                );
                              }}
                              className="border border-gray-300 p-1 rounded text-blue w-full"
                            />
                          </div>
                          <div className="flex flex-wrap flex-row justify-between w-full mb-5">
                            <span className="text-gray-500">Gross Weight</span>
                            <input
                              type="text"
                              value={
                                selectedShipment.shipment_grossweight || ""
                              }
                              onChange={(e) => {
                                const newValue = e.target.value;
                                handleFieldChange(
                                  "shipment",
                                  "shipment_grossweight",
                                  newValue
                                );
                                handleSaveField(
                                  "shipment",
                                  "shipment_grossweight",
                                  newValue
                                );
                              }}
                              className="border border-gray-300 p-1 rounded text-blue w-full"
                            />
                          </div>
                          <div className="flex flex-wrap flex-row justify-between w-full mb-5">
                            <span className="text-gray-500">Volume</span>
                            <input
                              type="text"
                              value={selectedShipment.shipment_volume || ""}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                handleFieldChange(
                                  "shipment",
                                  "shipment_volume",
                                  newValue
                                );
                                handleSaveField(
                                  "shipment",
                                  "shipment_volume",
                                  newValue
                                );
                              }}
                              className="border border-gray-300 p-1 rounded text-blue w-full"
                            />
                          </div>
                          <div className="flex flex-wrap flex-row justify-between w-full mb-5">
                            <span className="text-gray-500">Packages</span>
                            <input
                              type="text"
                              value={selectedShipment.shipment_packages || ""}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                handleFieldChange(
                                  "shipment",
                                  "shipment_packages",
                                  newValue
                                );
                                handleSaveField(
                                  "shipment",
                                  "shipment_packages",
                                  newValue
                                );
                              }}
                              className="border border-gray-300 p-1 rounded text-blue w-full"
                            />
                          </div>
                        </>
                      )}
                      {selectedShipment.shipment_terms === "LCL" && (
                        <>
                          <div className="flex flex-wrap flex-row justify-between w-full mb-5">
                            <span className="text-gray-500">Packages</span>
                            <input
                              type="text"
                              value={selectedShipment.shipment_packages || ""}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                handleFieldChange(
                                  "shipment",
                                  "shipment_packages",
                                  newValue
                                );
                                handleSaveField(
                                  "shipment",
                                  "shipment_packages",
                                  newValue
                                );
                              }}
                              className="border border-gray-300 p-1 rounded text-blue w-full"
                            />
                          </div>
                          <div className="flex flex-wrap flex-row justify-between w-full mb-5">
                            <span className="text-gray-500">Volume</span>
                            <input
                              type="text"
                              value={selectedShipment.shipment_volume || ""}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                handleFieldChange(
                                  "shipment",
                                  "shipment_volume",
                                  newValue
                                );
                                handleSaveField(
                                  "shipment",
                                  "shipment_volume",
                                  newValue
                                );
                              }}
                              className="border border-gray-300 p-1 rounded text-blue w-full"
                            />
                          </div>
                          <div className="flex flex-wrap flex-row justify-between w-full mb-5">
                            <span className="text-gray-500">Gross Weight</span>
                            <input
                              type="text"
                              value={
                                selectedShipment.shipment_grossweight || ""
                              }
                              onChange={(e) => {
                                const newValue = e.target.value;
                                handleFieldChange(
                                  "shipment",
                                  "shipment_grossweight",
                                  newValue
                                );
                                handleSaveField(
                                  "shipment",
                                  "shipment_grossweight",
                                  newValue
                                );
                              }}
                              className="border border-gray-300 p-1 rounded text-blue w-full"
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
              {selectedShipment.shipment_type === "BROKERAGE" && (
                <div className="flex flex-col w-full mb-5">
                  {/* Dropdown to select Brokerage Remark */}
                  {/* <span className="text-gray-500">Add Brokerage Remark</span> */}
                  {/* <select
                    value={draftBrokerageRemarks.brokerage_title}
                    onChange={(e) =>
                      handleBrokerageFieldChange(
                        "brokerage_title",
                        e.target.value
                      )
                    }
                    className="border border-gray-300 p-1 rounded text-blue w-full"
                  >
                    <option value="" disabled>
                      Select Brokerage Remark
                    </option>
                    {remarksOptions.map((option, index) => (
                      <option key={index} value={option}>
                        {option}
                      </option>
                    ))}
                  </select> */}

                  {/* Conditional input field based on selected brokerage_title */}
                  {/* {draftBrokerageRemarks.brokerage_title && (
                    <input
                      type="text"
                      placeholder={`Enter remarks for ${draftBrokerageRemarks.brokerage_title}`}
                      value={draftBrokerageRemarks.brokerage_input}
                      onChange={(e) =>
                        handleBrokerageFieldChange(
                          "brokerage_input",
                          e.target.value
                        )
                      }
                      className="border border-gray-300 p-1 rounded text-blue w-full mt-2"
                    />
                  )} */}
                  {/* Brokerage Remarks History */}
                  {/* <span className="text-gray-500 mb-2">
                    Brokerage Remarks History
                  </span> */}
                  {/* {selectedShipment?.shipment_type === "BROKERAGE" && (
                    <div className="flex flex-col w-full mb-5">
                      {selectedShipment.brokerage_remarks?.length > 0 ? (
                        <ul className="mb-3 border border-gray-300 rounded-lg p-4 max-h-60 overflow-y-auto bg-gray-50 shadow-lg">
                          {selectedShipment.brokerage_remarks.map(
                            (remark, index) => (
                              <li
                                key={index}
                                className="mb-3 p-2 border-b border-gray-200 last:border-0 flex justify-between items-start"
                              >
                                <div className="flex flex-col w-full">
                                  <span className="text-xs text-gray-500 mb-1">
                                    {new Date(
                                      remark.timestamp
                                    ).toLocaleDateString("en-US", {
                                      weekday: "long",
                                      year: "numeric",
                                      month: "long",
                                      day: "numeric",
                                      timeZone: "Asia/Manila",
                                    })}
                                    ,{" "}
                                    {new Date(
                                      remark.timestamp
                                    ).toLocaleTimeString("en-US", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: true,
                                      timeZone: "Asia/Manila",
                                    })}
                                  </span>
                                  <span className="font-semibold text-blue text-lg">
                                    {remark.brokerage_title}
                                  </span>
                                  {editBrokerageIndex === index ? (
                                    <textarea
                                      className="border border-gray-300 rounded p-2 mt-1 w-full resize-none"
                                      rows={3}
                                      value={tempBrokerageRemark}
                                      onChange={(e) =>
                                        handleInputChangeBrokerage(
                                          e.target.value
                                        )
                                      }
                                    />
                                  ) : (
                                    <p className="text-gray-700 mt-1">
                                      {remark.brokerage_input ||
                                        "No additional remarks"}
                                    </p>
                                  )}
                                </div>
                                {/* Buttons */}
                  {/* <div className="flex gap-2 ml-3">
                                  {editBrokerageIndex === index ? (
                                    <button
                                      className="text-green-600 hover:text-green-800 text-sm"
                                      onClick={handleSaveBrokerageClick}
                                    >
                                      Save
                                    </button>
                                  ) : (
                                    <button
                                      className="text-blue-600 hover:text-blue-800 text-sm"
                                      onClick={() =>
                                        handleEditBrokerageClick(index)
                                      }
                                    >
                                      Edit
                                    </button>
                                  )}
                                  <button
                                    className="text-red-600 hover:text-red-800 text-sm"
                                    onClick={() =>
                                      handleDeleteBrokerageRemark(index)
                                    }
                                  >
                                    Delete
                                  </button>
                                </div>
                              </li>
                            )
                          )}
                        </ul>
                      ) : (
                        <span className="text-sm text-gray-400 mb-3">
                          No previous brokerage remarks available.
                        </span>
                      )}
                    </div>
                  )} */}
                </div>
              )}

              {selectedShipment?.shipment_type === "AIR" && (
                <div className="flex flex-col w-full mb-5">
                  <span className="text-gray-500 mb-2">
                    Cargo/Shipment Status History
                  </span>
                  {selectedShipment.remarks?.length > 0 ? (
                    <ul className="mb-3 border border-gray-300 rounded-lg p-4 max-h-60 overflow-y-auto bg-gray-50 shadow-lg">
                      {selectedShipment.remarks.map((remark, index) => (
                        <li
                          key={index}
                          className="mb-3 p-2 border-b border-gray-200 last:border-0 flex justify-between items-start"
                        >
                          <div className="flex flex-col w-full">
                            <span className="text-xs text-gray-500 mb-1">
                              {new Date(remark.date).toLocaleDateString(
                                "en-US",
                                {
                                  weekday: "long",
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                  timeZone: "Asia/Manila",
                                }
                              )}
                              ,{" "}
                              {new Date(remark.date).toLocaleTimeString(
                                "en-US",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                  timeZone: "Asia/Manila",
                                }
                              )}
                            </span>
                            <span className="font-semibold text-blue text-lg">
                              {remark.status}
                            </span>
                            {editIndex === index ? (
                              <textarea
                                className="border border-gray-300 rounded p-2 mt-1 w-full resize-none"
                                rows={3}
                                value={tempRemark}
                                onChange={(e) =>
                                  handleInputChangeRemarks(e.target.value)
                                }
                              />
                            ) : (
                              <div className="text-gray-700 mt-1 whitespace-pre-wrap">
                                {renderSavedShipmentRemark(remark)}
                              </div>
                            )}
                          </div>
                          {/* Buttons */}
                          <div className="flex gap-2 ml-3">
                            {editIndex === index ? (
                              <button
                                className="text-green-600 hover:text-green-800 text-sm"
                                onClick={handleSaveClick}
                              >
                                Save
                              </button>
                            ) : (
                              <button
                                className="text-blue-600 hover:text-blue-800 text-sm"
                                onClick={() => handleEditClick(index)}
                              >
                                Edit
                              </button>
                            )}
                            <button
                              className="text-red-600 hover:text-red-800 text-sm"
                              onClick={() => handleDeleteRemark(index)}
                            >
                              Delete
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-sm text-gray-400 mb-3">
                      No previous remarks available.
                    </span>
                  )}
                </div>
              )}
              {selectedShipment?.shipment_type === "LAND" && (
                <div className="flex flex-col w-full mb-5">
                  <span className="text-gray-500 mb-2">
                    Cargo/Shipment Status History
                  </span>
                  {selectedShipment.remarks?.length > 0 ? (
                    <ul className="mb-3 border border-gray-300 rounded-lg p-4 max-h-60 overflow-y-auto bg-gray-50 shadow-lg">
                      {selectedShipment.remarks.map((remark, index) => (
                        <li
                          key={index}
                          className="mb-3 p-2 border-b border-gray-200 last:border-0 flex justify-between items-start"
                        >
                          <div className="flex flex-col w-full">
                            <span className="text-xs text-gray-500 mb-1">
                              {new Date(remark.date).toLocaleDateString(
                                "en-US",
                                {
                                  weekday: "long",
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                  timeZone: "Asia/Manila",
                                }
                              )}
                              ,{" "}
                              {new Date(remark.date).toLocaleTimeString(
                                "en-US",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                  timeZone: "Asia/Manila",
                                }
                              )}
                            </span>

                            <span className="font-semibold text-blue text-lg">
                              {remark.status}
                            </span>

                            {editIndex === index ? (
                              <textarea
                                className="border border-gray-300 rounded p-2 mt-1 w-full resize-none"
                                rows={3}
                                value={tempRemark}
                                onChange={(e) =>
                                  handleInputChangeRemarks(e.target.value)
                                }
                              />
                            ) : (
                              <div className="text-gray-700 mt-1 whitespace-pre-wrap">
                                {renderSavedShipmentRemark(remark)}
                              </div>
                            )}
                          </div>

                          {/* Buttons */}
                          <div className="flex gap-2 ml-3">
                            {editIndex === index ? (
                              <button
                                className="text-green-600 hover:text-green-800 text-sm"
                                onClick={handleSaveClick}
                              >
                                Save
                              </button>
                            ) : (
                              <button
                                className="text-blue-600 hover:text-blue-800 text-sm"
                                onClick={() => handleEditClick(index)}
                              >
                                Edit
                              </button>
                            )}
                            <button
                              className="text-red-600 hover:text-red-800 text-sm"
                              onClick={() => handleDeleteRemark(index)}
                            >
                              Delete
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-sm text-gray-400 mb-3">
                      No previous remarks available.
                    </span>
                  )}
                </div>
              )}
              {selectedShipment?.shipment_type === "DOMESTIC" && (
                <div className="flex flex-col w-full mb-5">
                  <span className="text-gray-500 mb-2">
                    Cargo/Shipment Status History
                  </span>
                  {selectedShipment.remarks?.length > 0 ? (
                    <ul className="mb-3 border border-gray-300 rounded-lg p-4 max-h-60 overflow-y-auto bg-gray-50 shadow-lg">
                      {selectedShipment.remarks.map((remark, index) => (
                        <li
                          key={index}
                          className="mb-3 p-2 border-b border-gray-200 last:border-0 flex justify-between items-start"
                        >
                          <div className="flex flex-col w-full">
                            <span className="text-xs text-gray-500 mb-1">
                              {new Date(remark.date).toLocaleDateString(
                                "en-US",
                                {
                                  weekday: "long",
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                  timeZone: "Asia/Manila",
                                }
                              )}
                              ,{" "}
                              {new Date(remark.date).toLocaleTimeString(
                                "en-US",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                  timeZone: "Asia/Manila",
                                }
                              )}
                            </span>
                            <span className="font-semibold text-blue text-lg">
                              {remark.status}
                            </span>
                            {editIndex === index ? (
                              <textarea
                                className="border border-gray-300 rounded p-2 mt-1 w-full resize-none"
                                rows={3}
                                value={tempRemark}
                                onChange={(e) =>
                                  handleInputChangeRemarks(e.target.value)
                                }
                              />
                            ) : (
                              <div className="text-gray-700 mt-1 whitespace-pre-wrap">
                                {renderSavedShipmentRemark(remark)}
                              </div>
                            )}
                          </div>
                          {/* Buttons */}
                          <div className="flex gap-2 ml-3">
                            {editIndex === index ? (
                              <button
                                className="text-green-600 hover:text-green-800 text-sm"
                                onClick={handleSaveClick}
                              >
                                Save
                              </button>
                            ) : (
                              <button
                                className="text-blue-600 hover:text-blue-800 text-sm"
                                onClick={() => handleEditClick(index)}
                              >
                                Edit
                              </button>
                            )}
                            <button
                              className="text-red-600 hover:text-red-800 text-sm"
                              onClick={() => handleDeleteRemark(index)}
                            >
                              Delete
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-sm text-gray-400 mb-3">
                      No previous remarks available.
                    </span>
                  )}
                </div>
              )}
              {selectedShipment?.shipment_type === "BROKERAGE" && (
                <div className="flex flex-col w-full mb-5">
                  <span className="text-gray-500 mb-2">
                    Cargo/Shipment Status History
                  </span>
                  {selectedShipment.remarks?.length > 0 ? (
                    <ul className="mb-3 border border-gray-300 rounded-lg p-4 max-h-60 overflow-y-auto bg-gray-50 shadow-lg">
                      {selectedShipment.remarks.map((remark, index) => (
                        <li
                          key={index}
                          className="mb-3 p-2 border-b border-gray-200 last:border-0 flex justify-between items-start"
                        >
                          <div className="flex flex-col w-full">
                            <span className="text-xs text-gray-500 mb-1">
                              {new Date(remark.date).toLocaleDateString(
                                "en-US",
                                {
                                  weekday: "long",
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                  timeZone: "Asia/Manila",
                                }
                              )}
                              ,{" "}
                              {new Date(remark.date).toLocaleTimeString(
                                "en-US",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                  timeZone: "Asia/Manila",
                                }
                              )}
                            </span>
                            <span className="font-semibold text-blue text-lg">
                              {remark.status}
                            </span>
                            {editIndex === index ? (
                              <textarea
                                className="border border-gray-300 rounded p-2 mt-1 w-full resize-none"
                                rows={3}
                                value={tempRemark}
                                onChange={(e) =>
                                  handleInputChangeRemarks(e.target.value)
                                }
                              />
                            ) : (
                              <div className="text-gray-700 mt-1 whitespace-pre-wrap">
                                {renderSavedShipmentRemark(remark)}
                              </div>
                            )}
                          </div>
                          {/* Buttons */}
                          <div className="flex gap-2 ml-3">
                            {editIndex === index ? (
                              <button
                                className="text-green-600 hover:text-green-800 text-sm"
                                onClick={handleSaveClick}
                              >
                                Save
                              </button>
                            ) : (
                              <button
                                className="text-blue-600 hover:text-blue-800 text-sm"
                                onClick={() => handleEditClick(index)}
                              >
                                Edit
                              </button>
                            )}
                            <button
                              className="text-red-600 hover:text-red-800 text-sm"
                              onClick={() => handleDeleteRemark(index)}
                            >
                              Delete
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-sm text-gray-400 mb-3">
                      No previous remarks available.
                    </span>
                  )}
                </div>
              )}
              {selectedShipment?.shipment_type === "SEA" && (
                <div className="flex flex-col w-full mb-5">
                  <span className="text-gray-500 mb-2">
                    Cargo/Shipment Status History
                  </span>
                  {selectedShipment.remarks?.length > 0 ? (
                    <ul className="mb-3 border border-gray-300 rounded-lg p-4 max-h-60 overflow-y-auto bg-gray-50 shadow-lg">
                      {selectedShipment.remarks.map((remark, index) => (
                        <li
                          key={index}
                          className="mb-3 p-2 border-b border-gray-200 last:border-0 flex justify-between items-start"
                        >
                          <div className="flex flex-col w-full">
                            <span className="text-xs text-gray-500 mb-1">
                              {new Date(remark.date).toLocaleDateString(
                                "en-US",
                                {
                                  weekday: "long",
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                  timeZone: "Asia/Manila",
                                }
                              )}
                              ,{" "}
                              {new Date(remark.date).toLocaleTimeString(
                                "en-US",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                  timeZone: "Asia/Manila",
                                }
                              )}
                            </span>
                            <span className="font-semibold text-blue text-lg">
                              {remark.status}
                            </span>
                            {editIndex === index ? (
                              <textarea
                                className="border border-gray-300 rounded p-2 mt-1 w-full resize-none"
                                rows={3}
                                value={tempRemark}
                                onChange={(e) =>
                                  handleInputChangeRemarks(e.target.value)
                                }
                              />
                            ) : (
                              <div className="text-gray-700 mt-1 whitespace-pre-wrap">
                                {renderSavedShipmentRemark(remark)}
                              </div>
                            )}
                          </div>
                          {/* Buttons */}
                          <div className="flex gap-2 ml-3">
                            {editIndex === index ? (
                              <button
                                className="text-green-600 hover:text-green-800 text-sm"
                                onClick={handleSaveClick}
                              >
                                Save
                              </button>
                            ) : (
                              <button
                                className="text-blue-600 hover:text-blue-800 text-sm"
                                onClick={() => handleEditClick(index)}
                              >
                                Edit
                              </button>
                            )}
                            <button
                              className="text-red-600 hover:text-red-800 text-sm"
                              onClick={() => handleDeleteRemark(index)}
                            >
                              Delete
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-sm text-gray-400 mb-3">
                      No previous remarks available.
                    </span>
                  )}
                </div>
              )}
              {/* Always Visible Section */}
              <div>
                <div className="flex flex-wrap flex-row justify-between w-full mb-5">
                  <span className="text-gray-500">Status</span>
                  <select
                    value={editableStatus}
                    onChange={(e) => setEditableStatus(e.target.value)}
                    className="border border-gray-300 p-1 rounded text-blue w-full w-[50%] font-bold"
                  >
                    <option className="text-gray-500" value="">
                      Select status
                    </option>
                    {getFilteredStatuses(
                      selectedShipment?.delivery_mode,
                      selectedShipment?.shipment_type
                    ).map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
                {editableStatus === "In Transit" && (
                  <div className="flex flex-col w-full mb-5">
                    <div className="flex flex-row justify-between mb-3">
                      <span className="text-gray-500">In Transit</span>
                      <select
                        value={transitType} // State for dropdown selection
                        onChange={(e) => setTransitType(e.target.value)}
                        className="border border-gray-300 py-2 px-3 rounded text-blue"
                      >
                        <option value="">Select Type</option>
                        <option value="Arrival">Arrival</option>
                        <option value="Departure">Departure</option>
                      </select>
                    </div>
                    {transitType && (
                      <div className="flex flex-row justify-between mb-3">
                        <span className="text-gray-500">{`Date ${transitType}`}</span>
                        <input
                          type="datetime-local" // Updated to datetime-local for date and time input
                          value={transitDate} // State for the date-time input
                          onChange={(e) => setTransitDate(e.target.value)}
                          className="border border-gray-300 py-2 px-3 rounded text-blue"
                        />
                      </div>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap flex-row justify-between w-full mb-5">
                  <span className="text-gray-500">Transfer to</span>
                  <select
                    value={editableTranserTo}
                    onChange={(e) => setEditableTransferTo(e.target.value)}
                    className="border border-gray-300 p-1 rounded text-blue w-full w-[50%] font-bold"
                  >
                    <option className="text-gray-500" value="">
                      Choose employee
                    </option>
                    {fetchEmployees.map((employee) => (
                      <option key={employee.user_id} value={employee.user_id}>
                        {employee.user_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-wrap flex-row justify-between w-full mb-5">
                  <span className="text-gray-500">Job Order</span>
                  <input
                    type="text"
                    value={selectedShipment.job_order || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        "shipments",
                        "job_order",
                        e.target.value
                      )
                    } // Provide section and field
                    onBlur={(e) =>
                      handleSaveField("shipments", "job_order", e.target.value)
                    } // Provide section and field
                    className="border border-gray-300 py-2 px-3 rounded text-blue focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Auto Generated Code"
                  />
                </div>
                <div className="flex flex-wrap flex-row justify-between w-full mb-5">
                  <span className="text-gray-500">WayBill Number</span>
                  <input
                    type="text"
                    value={selectedShipment.tracking_no || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        "shipments",
                        "tracking_no",
                        e.target.value
                      )
                    } // Provide section and field
                    onBlur={(e) =>
                      handleSaveField(
                        "shipments",
                        "tracking_no",
                        e.target.value
                      )
                    } // Provide section and field
                    className="border border-gray-300 py-2 px-3 rounded text-blue focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter job order"
                  />
                </div>
                <div className="flex flex-wrap flex-row justify-between w-full mb-5">
                  <span className="text-gray-500">Master Waybill</span>
                  <input
                    type="text"
                    value={selectedShipment.master_waybill || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        "shipments",
                        "master_waybill",
                        e.target.value
                      )
                    } // Provide section and field
                    onBlur={(e) =>
                      handleSaveField(
                        "shipments",
                        "master_waybill",
                        e.target.value
                      )
                    } // Provide section and field
                    className="border border-gray-300 py-2 px-3 rounded text-blue focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter master waybill"
                  />
                </div>
                <div className="flex flex-wrap flex-row justify-between w-full mb-5">
                  <span className="text-gray-500">House Waybill</span>
                  <input
                    type="text"
                    value={selectedShipment.house_waybill || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        "shipments",
                        "house_waybill",
                        e.target.value
                      )
                    } // Provide section and field
                    onBlur={(e) =>
                      handleSaveField(
                        "shipments",
                        "house_waybill",
                        e.target.value
                      )
                    } // Provide section and field
                    className="border border-gray-300 py-2 px-3 rounded text-blue focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter house waybill"
                  />
                </div>

                {getFilteredStatuses(
                  selectedShipment?.delivery_mode,
                  selectedShipment?.shipment_type
                ).includes(editableStatus) && (
                  <div className="flex flex-col mb-5">
                    <label className="text-gray-500 mb-1">Remarks</label>
                    <div className="flex gap-2 mb-2">
                      <label className="flex items-center gap-1">
                        <input
                          type="radio"
                          value="predefined"
                          checked={newRemarkInputMode === "predefined"}
                          onChange={(e) => {
                            setNewRemarkInputMode(e.target.value);
                            setRemarks("");
                            setNewSelectedRemarkOption("");
                            setAdditionalRemarkInfo("");
                            setRemarkDate("");
                            setManualRemarkText("");
                            setRemarkFootnote("");
                          }}
                        />
                        <span className="text-sm">Predefined</span>
                      </label>
                      <label className="flex items-center gap-1">
                        <input
                          type="radio"
                          value="manual"
                          checked={newRemarkInputMode === "manual"}
                          onChange={(e) => {
                            setNewRemarkInputMode(e.target.value);
                            setRemarks("");
                            setNewSelectedRemarkOption("");
                            setAdditionalRemarkInfo("");
                            setRemarkDate("");
                            setManualRemarkText("");
                            setRemarkFootnote("");
                          }}
                        />
                        <span className="text-sm">Manual Input</span>
                      </label>
                    </div>
                    {newRemarkInputMode === "predefined" ? (
                      <>
                        <select
                          className="border border-gray-300 py-2 px-3 rounded text-blue"
                          value={newSelectedRemarkOption}
                          onChange={(e) => {
                            const selectedValue = e.target.value;
                            setNewSelectedRemarkOption(selectedValue);
                            setRemarks(
                              buildShipmentRemarkPreview({
                                dateValue: remarkDate,
                                remarkText: selectedValue,
                                additionalInfo: additionalRemarkInfo,
                                footnote: remarkFootnote,
                              })
                            );
                          }}
                        >
                          <option value="">Select a remark...</option>
                          {remarksOptions.map((option) => (
                            <option key={option} value={option}>
                              {option.replace(/_/g, " ")}
                            </option>
                          ))}
                        </select>
                        {newSelectedRemarkOption && (
                          <div className="mt-2 space-y-3">
                            {/* Date Picker */}
                            <div>
                              <label className="text-gray-500 text-sm mb-1 block">
                                Date (Optional)
                              </label>
                              <input
                                type="date"
                                value={remarkDate}
                                onChange={(e) => {
                                  const dateValue = e.target.value;
                                  setRemarkDate(dateValue);
                                  setRemarks(
                                    buildShipmentRemarkPreview({
                                      dateValue,
                                      remarkText: newSelectedRemarkOption,
                                      additionalInfo: additionalRemarkInfo,
                                      footnote: remarkFootnote,
                                    })
                                  );
                                }}
                                className="border border-gray-300 py-2 px-3 rounded text-blue w-full"
                              />
                            </div>

                            {/* Additional Information */}
                            <div>
                              <label className="text-gray-500 text-sm mb-1 block">
                                Additional Information (Optional)
                              </label>
                              <textarea
                                value={additionalRemarkInfo}
                                onChange={(e) => {
                                  const additionalInfo = e.target.value;
                                  setAdditionalRemarkInfo(additionalInfo);
                                  setRemarks(
                                    buildShipmentRemarkPreview({
                                      dateValue: remarkDate,
                                      remarkText: newSelectedRemarkOption,
                                      additionalInfo,
                                      footnote: remarkFootnote,
                                    })
                                  );
                                }}
                                className="border border-gray-300 py-2 px-3 rounded text-blue resize-none h-20 w-full"
                                placeholder="Add additional information here..."
                              />
                            </div>

                            <div>
                              <label className="text-gray-500 text-sm mb-1 block">
                                Footnote (Optional)
                              </label>
                              <textarea
                                value={remarkFootnote}
                                onChange={(e) => {
                                  const footnote = e.target.value;
                                  setRemarkFootnote(footnote);
                                  setRemarks(
                                    buildShipmentRemarkPreview({
                                      dateValue: remarkDate,
                                      remarkText: newSelectedRemarkOption,
                                      additionalInfo: additionalRemarkInfo,
                                      footnote,
                                    })
                                  );
                                }}
                                className="border border-gray-300 py-2 px-3 rounded text-blue resize-none h-20 w-full"
                                placeholder="Add footnote here..."
                              />
                            </div>

                            {/* Preview */}
                            {(remarkDate || additionalRemarkInfo || newSelectedRemarkOption || remarkFootnote) && (
                              <div className="bg-gray-50 border border-gray-200 rounded p-3">
                                <label className="text-gray-500 text-xs font-semibold mb-2 block">
                                  PREVIEW
                                </label>
                                <div className="text-blue text-sm whitespace-pre-wrap font-medium">
                                  {renderShipmentRemarkPreview(
                                    buildShipmentRemarkPreview({
                                      dateValue: remarkDate,
                                      remarkText: newSelectedRemarkOption,
                                      additionalInfo: additionalRemarkInfo,
                                      footnote: remarkFootnote,
                                    })
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="space-y-3">
                        {/* Date Picker */}
                        <div>
                          <label className="text-gray-500 text-sm mb-1 block">
                            Date (Optional)
                          </label>
                          <input
                            type="date"
                            value={remarkDate}
                            onChange={(e) => {
                              const dateValue = e.target.value;
                              setRemarkDate(dateValue);
                              setRemarks(
                                buildShipmentRemarkPreview({
                                  dateValue,
                                  remarkText: manualRemarkText,
                                  footnote: remarkFootnote,
                                })
                              );
                            }}
                            className="border border-gray-300 py-2 px-3 rounded text-blue w-full"
                          />
                        </div>

                        {/* Manual Remark Input */}
                        <div>
                          <label className="text-gray-500 text-sm mb-1 block">
                            Remark
                          </label>
                          <textarea
                            value={manualRemarkText}
                            onChange={(e) => {
                              const remarkText = e.target.value;
                              setManualRemarkText(remarkText);
                              setRemarks(
                                buildShipmentRemarkPreview({
                                  dateValue: remarkDate,
                                  remarkText,
                                  footnote: remarkFootnote,
                                })
                              );
                            }}
                            className="border border-gray-300 py-2 px-3 rounded text-blue resize-none h-32 w-full"
                            placeholder="Enter custom remark..."
                          />
                        </div>

                        <div>
                          <label className="text-gray-500 text-sm mb-1 block">
                            Footnote (Optional)
                          </label>
                          <textarea
                            value={remarkFootnote}
                            onChange={(e) => {
                              const footnote = e.target.value;
                              setRemarkFootnote(footnote);
                              setRemarks(
                                buildShipmentRemarkPreview({
                                  dateValue: remarkDate,
                                  remarkText: manualRemarkText,
                                  footnote,
                                })
                              );
                            }}
                            className="border border-gray-300 py-2 px-3 rounded text-blue resize-none h-20 w-full"
                            placeholder="Add footnote here..."
                          />
                        </div>

                        {/* Preview */}
                        {(remarkDate || manualRemarkText || remarkFootnote) && (
                          <div className="bg-gray-50 border border-gray-200 rounded p-3">
                            <label className="text-gray-500 text-xs font-semibold mb-2 block">
                              PREVIEW
                            </label>
                            <div className="text-blue text-sm whitespace-pre-wrap font-medium">
                              {renderShipmentRemarkPreview(
                                buildShipmentRemarkPreview({
                                  dateValue: remarkDate,
                                  remarkText: manualRemarkText,
                                  footnote: remarkFootnote,
                                })
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {/* {[
                  "Order Placed",
                  "In Warehouse",
                  "Departure at Origin",
                  "In Transit",
                  "Arrival at Destination",
                  "Custom Clearance",
                  "Delivered",
                ].includes(editableStatus) && (
                  <div className="flex flex-col mb-5">
                    <label className="text-gray-500 mb-1">Date of Shipment Received</label>
                    <input
                      type="datetime-local"
                      value={receivedDate}
                      onChange={(e) => setReceivedDate(e.target.value)}
                      className="border border-gray-300 py-2 px-3 rounded text-blue"
                    />
                  </div>
                )} */}

                {editableStatus === "Departure at Origin" && (
                  <div className="flex flex-wrap flex-row justify-between w-full mb-5">
                    <span className="text-gray-500">
                      Actual Departure Date and Time
                    </span>
                    <input
                      type="datetime-local"
                      value={actualDepartureDate}
                      onChange={(e) => setActualDepartureDate(e.target.value)}
                      className="text-blue"
                    />
                  </div>
                )}
                {editableStatus === "Arrival at Destination" && (
                  <div className="flex flex-wrap flex-row justify-between w-full mb-5">
                    <span className="text-gray-500">
                      Actual Arrival Date and Time
                    </span>
                    <input
                      type="datetime-local"
                      value={actualArrivalDate}
                      onChange={(e) => setActualArrivalDate(e.target.value)}
                      className="text-blue"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Save Button */}
            <div className="mt-4">
              <button
                onClick={async () => {
                  setSavingStatus(true); // Start loading
                  try {
                    // Run all functions sequentially and wait for them to complete
                    await handleStatusChange();
                    // console.log("Status updated successfully.");

                    if (editableTranserTo && selectedShipment.job_order) {
                      await handleTransferChange();
                      // console.log("Transfer updated successfully.");
                    }

                    // Save other fields as before
                    for (const field of editableShipperFields) {
                      try {
                        await handleSaveShipperField(
                          "shipper",
                          field,
                          selectedShipment[`shipper_${field.toLowerCase()}`]
                        );
                        // console.log(`Shipper field '${field}' updated successfully.`);
                      } catch (error) {
                        console.error(
                          `Error updating shipper field '${field}':`,
                          error
                        );
                        toast.error(
                          `Failed to update shipper field '${field}'.`
                        );
                      }
                    }

                    for (const field of editableReceiverFields) {
                      try {
                        await handleSaveReceiverField(
                          "receiver",
                          field,
                          selectedShipment[`receiver_${field.toLowerCase()}`]
                        );
                        // console.log(`Receiver field '${field}' updated successfully.`);
                      } catch (error) {
                        // console.error(`Error updating receiver field '${field}':`, error);
                        toast.error(
                          `Failed to update receiver field '${field}'.`
                        );
                      }
                    }

                    for (const field of editableShipmentFields) {
                      try {
                        await handleSaveField(
                          "shipment",
                          field,
                          selectedShipment[`shipment_${field.toLowerCase()}`]
                        );
                        // console.log(`Shipment field '${field}' updated successfully.`);
                      } catch (error) {
                        // console.error(`Error updating shipment field '${field}':`, error);
                        toast.error(
                          `Failed to update shipment field '${field}'.`
                        );
                      }
                    }

                    // Save brokerage remarks fields
                    if (draftBrokerageRemarks) {
                      await handleSaveBrokerageField();
                      // console.log('Brokerage remarks updated successfully.');
                    }

                    // Show success toast notification after all operations complete
                    toast.success("Fields updated successfully!");

                    // Close modal only after successful save
                    closeModal();
                  } catch (error) {
                    console.error("Error updating fields:", error);
                    toast.error(
                      "An unexpected error occurred while updating fields."
                    );
                  } finally {
                    setSavingStatus(false); // Stop loading
                  }
                }}
                disabled={savingStatus}
                className="bg-blue text-white px-6 py-2 rounded hover:bg-blue/90 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 transition-all"
              >
                {savingStatus ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  "Save Status"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {uploadModalOpen && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-40">
          <div className="bg-white w-[90%] max-w-md p-6 rounded-lg shadow-lg relative">
            <button
              onClick={closeUploadModal}
              className="absolute top-2 right-2 text-gray-500 text-[30px] hover:text-gray-700"
            >
              &times;
            </button>
            <h2 className="text-lg font-semibold text-blue">
              Upload Document for {selectedShipmentForUpload.tracking_no}
            </h2>
            <div className="mt-4">
              {/* Document Title Input */}
              <input
                type="text"
                placeholder="Enter document title"
                value={documentTitle}
                onChange={(e) => setDocumentTitle(e.target.value)}
                className="border border-gray-300 p-2 rounded w-full mb-4"
              />

              <select
                value={documentSection}
                onChange={(e) => setDocumentSection(e.target.value)}
                className="border border-gray-300 p-2 rounded w-full mb-4"
              >
                <option value="">Select document section</option>
                {DOCUMENT_SECTIONS.map((section) => (
                  <option key={section} value={section}>{section}</option>
                ))}
              </select>

              {/* File Input */}
              <input
                type="file"
                onChange={handleFileChange}
                className="border border-gray-300 p-2 rounded w-full mb-4"
              />

              {/* Upload Button */}
              <div className="flex justify-between mt-6">
                <button
                  onClick={closeUploadModal}
                  className="bg-gray-300 px-4 py-2 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={loading2} // Disable button if loading
                  className={`w-1/2 ${
                    loading2
                      ? "bg-gray-300 text-blue"
                      : "bg-blue px-4 py-2 rounded"
                  } text-yellow text-[16px] font-semibold py-2 rounded-md hover:bg-yellow hover:text-blue`}
                >
                  {loading ? (
                    <i className="fas fa-spinner fa-spin"></i> // Display spinner when loading
                  ) : (
                    "Upload"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {mounted &&
        portalContainer &&
        viewModalOpen &&
        selectedShipmentForView &&
        createPortal(
          <div
            className="fixed inset-0 w-screen h-screen bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            role="dialog"
            aria-modal="true"
          >
            <div
              className="bg-white w-full max-w-lg mx-auto p-6 rounded-lg shadow-2xl relative max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={closeViewModal}
                className="absolute top-2 right-2 text-gray-500 text-[30px] hover:text-gray-700 z-10"
                aria-label="Close"
              >
                &times;
              </button>

              {/* Modal Title */}
              <h2 className="text-lg font-semibold text-blue-600 mb-2">
                Viewing Documents for{" "}
                {selectedShipmentForView?.tracking_no || "Loading..."}
              </h2>

              {/* Shipment ID */}
              <p className="text-sm text-blue-700 mb-4">
                <strong>Shipment ID:</strong>{" "}
                {selectedShipmentForView?.shipment_id || "Loading..."}
              </p>

              <DocumentSectionTabs
                activeSection={activeDocumentSection}
                onChange={(section) => { setActiveDocumentSection(section); setCurrentPage(1); }}
              />

              {/* Document List */}
              {filteredDocuments.length > 0 ? (
                <div className="document-list">
                  {currentDocuments.map((doc) => {
                    const sent = isDocumentSent(doc);
                    return (
                    <div key={doc.id} className="mb-4 border-b pb-2">
                      <p className="text-md text-blue-600 font-medium">
                        <strong>Title:</strong>{" "}
                        {doc.document_title || "No Title Provided"}
                        {sent && (
                          <span className="ml-2 inline-block bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full align-middle">
                            Sent
                          </span>
                        )}
                      </p>
                      <p className="text-md text-blue-600 font-medium">
                        <strong>Document Name:</strong>{" "}
                        {doc.document_name || "No Document Name"}
                      </p>

                      <a
                        href={doc.document_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Download
                      </a>

                      {user?.role !== "Corporate" && (
                        <div className="flex gap-2 mt-2">
                          {sent ? (
                            <button
                              onClick={() =>
                                handleUnsendDocumentConfirmation(doc)
                              }
                              className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 text-sm"
                            >
                              Unsend
                            </button>
                          ) : (
                            <button
                              onClick={() =>
                                handleSendDocumentConfirmation(doc)
                              }
                              className="bg-blue text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
                            >
                              Send
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteDocument(doc)}
                            className="bg-red text-white px-4 py-2 rounded hover:bg-red-700 text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-600">
                  No documents available for this shipment.
                </p>
              )}

              {/* Pagination / Document Preview */}
              <div className="sticky bottom-0 z-10 flex items-center justify-between gap-3 mt-6 -mx-6 px-6 py-3 bg-white border-t">
                <button onClick={handlePrevPage} disabled={currentPage === 1} className="bg-gray-300 px-4 py-2 rounded disabled:opacity-50 text-sm">← Prev</button>
                <button type="button" onClick={openAllDocumentPreviews} disabled={filteredDocuments.length === 0} className="bg-blue text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 text-sm mx-auto">View All Documents ({filteredDocuments.length})</button>
                <button onClick={handleNextPage} disabled={currentDocuments.length < documentsPerPage} className="bg-gray-300 px-4 py-2 rounded disabled:opacity-50 text-sm">Next →</button>
              </div>
            </div>
          </div>,
          portalContainer
        )}

      {mounted &&
        portalContainer &&
        isHistoryModalOpen &&
        selectedShipment &&
        createPortal(
          <div className="fixed inset-0 z-40 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 relative">
              <button
                onClick={() => setIsHistoryModalOpen(false)}
                className="absolute top-3 right-3 text-gray-500 hover:text-red-600 z-10"
              >
                <i className="fa-solid fa-times"></i>
              </button>

              <h2 className="font-poppins text-xl font-semibold text-blue text-center mb-4">
                Cargo/Shipment Status History
              </h2>

              <div className="overflow-x-auto">
                <div className="relative">
                  <table className="table-auto w-full border border-gray-300 text-sm md:text-base table-fixed">
                    <thead className="bg-gray-200 hidden sm:table-header-group">
                      <tr>
                        <th className="w-[33.3%] px-4 py-2 text-left font-semibold text-gray-700">
                          Status
                        </th>
                        <th className="w-[33.3%] px-4 py-2 text-left font-semibold text-gray-700">
                          Remarks
                        </th>
                      </tr>
                    </thead>
                  </table>

                  <div className="max-h-[400px] overflow-y-auto">
                    <table className="table-auto w-full border border-gray-300 text-sm md:text-base table-fixed">
                      <tbody>
                        {parseRemarks().length > 0 ? (
                          parseRemarks()
                            .sort((a, b) => new Date(b.date) - new Date(a.date))
                            .map((remark, index) => (
                              <tr key={index}>
                                <td className="px-4 py-2 border">
                                  {remark.status}
                                </td>
                                <td className="px-4 py-2 border">
                                  {remark.status === "In Transit" ? (
                                    <>
                                      {renderVisibleShipmentRemark(
                                        remark.remark
                                      ) || "No remarks available"}
                                      <br />
                                    </>
                                  ) : remark.remark ? (
                                    renderVisibleShipmentRemark(remark.remark)
                                  ) : (
                                    "No remarks available"
                                  )}

                                  {remark.status === "Order Placed" && (
                                    <p className="text-sm text-blue-700 mb-4">
                                      <strong>
                                        Shipment Nomination Date:{" "}
                                      </strong>
                                      {remark.receivedDate
                                        ? (() => {
                                            const utcDate = new Date(
                                              remark.receivedDate
                                            );
                                            const fixedDate = new Date(
                                              utcDate.getTime() -
                                                8 * 60 * 60 * 1000
                                            );
                                            return fixedDate.toLocaleString(
                                              "en-US",
                                              {
                                                month: "long",
                                                day: "numeric",
                                                year: "numeric",
                                                hour: "numeric",
                                                minute: "numeric",
                                                hour12: true,
                                              }
                                            );
                                          })()
                                        : "Not set"}
                                    </p>
                                  )}

                                  {[
                                    "Order Placed",
                                    "Departure at Origin",
                                  ].includes(remark.status) && (
                                    <>
                                      <strong>ETD: </strong>
                                      {selectedShipment.etd
                                        ? new Date(
                                            selectedShipment.etd
                                          ).toLocaleString("en-US", {
                                            month: "long",
                                            day: "numeric",
                                            year: "numeric",
                                            hour: "numeric",
                                            minute: "numeric",
                                            timeZone: "Asia/Manila",
                                            hour12: true,
                                          })
                                        : "No remarks available"}
                                      <br />
                                    </>
                                  )}

                                  {remark.status === "Departure at Origin" && (
                                    <>
                                      <strong>ATD: </strong>
                                      {remark.atd
                                        ? new Date(remark.atd).toLocaleString(
                                            "en-US",
                                            {
                                              month: "long",
                                              day: "numeric",
                                              year: "numeric",
                                              hour: "numeric",
                                              minute: "numeric",
                                              timeZone: "Asia/Manila",
                                              hour12: true,
                                            }
                                          )
                                        : "No remarks available"}
                                      <br />
                                    </>
                                  )}

                                  {[
                                    "Order Placed",
                                    "Arrival at Destination",
                                  ].includes(remark.status) && (
                                    <>
                                      <strong>ETA: </strong>
                                      {selectedShipment.eta
                                        ? new Date(
                                            selectedShipment.eta
                                          ).toLocaleString("en-US", {
                                            month: "long",
                                            day: "numeric",
                                            year: "numeric",
                                            hour: "numeric",
                                            minute: "numeric",
                                            timeZone: "Asia/Manila",
                                            hour12: true,
                                          })
                                        : "No remarks available"}
                                      <br />
                                    </>
                                  )}

                                  {remark.status ===
                                    "Arrival at Destination" && (
                                    <>
                                      <strong>ATA: </strong>
                                      {remark.aad
                                        ? new Date(remark.aad).toLocaleString(
                                            "en-US",
                                            {
                                              month: "long",
                                              day: "numeric",
                                              year: "numeric",
                                              hour: "numeric",
                                              minute: "numeric",
                                              timeZone: "Asia/Manila",
                                              hour12: true,
                                            }
                                          )
                                        : "No remarks available"}
                                      <br />
                                    </>
                                  )}
                                </td>
                              </tr>
                            ))
                        ) : (
                          <tr>
                            <td
                              colSpan="2"
                              className="text-center py-4 text-gray-500"
                            >
                              No Cargo/Shipment Status History available.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          portalContainer
        )}

      {/* Details Modal */}
      {mounted &&
        portalContainer &&
        isDetailsModalOpen &&
        selectedShipment &&
        createPortal(
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-40 p-4">
            <div className="bg-white w-[95%] sm:w-[80%] md:w-[60%] lg:w-[50%] p-6 rounded-2xl shadow-2xl overflow-y-auto max-h-[85vh] relative border border-blue/20">
              {/* Close Button */}
              <button
                onClick={closeDetailsModal}
                className="absolute top-3 right-3 text-gray-400 hover:text-blue text-2xl font-bold z-10"
              >
                &times;
              </button>

              {/* Title */}
              <h2 className="text-2xl font-semibold text-blue mb-4 text-center top-0 bg-white z-10 pb-2 border-b">
                Shipment Details
              </h2>

              {/* Sections */}
              <div className="space-y-6 text-[15px] leading-relaxed text-gray-700">
                {/* Shipper Details */}
                <section className="bg-blue-50 border border-blue/30 rounded-lg p-4">
                  <h3 className="text-lg font-bold text-blue mb-3">
                    Shipper Details
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { label: "Name", value: selectedShipment.shipper_name },
                      { label: "Unit", value: selectedShipment.shipper_unit },
                      { label: "Email", value: selectedShipment.shipper_email },
                      { label: "City", value: selectedShipment.shipper_city },
                      {
                        label: "Street",
                        value: selectedShipment.shipper_street,
                      },
                      {
                        label: "Country",
                        value: selectedShipment.shipper_country,
                      },
                      { label: "Phone", value: selectedShipment.shipper_phone },
                      {
                        label: "Building",
                        value: selectedShipment.shipper_building,
                      },
                    ].map((item, index) => (
                      <div key={index} className="flex flex-col">
                        <span className="text-sm text-gray-600">
                          {item.label}
                        </span>
                        <span className="text-base font-bold text-gray-900">
                          {item.value || "-"}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Receiver Details */}
                <section className="bg-blue-50 border border-blue/30 rounded-lg p-4">
                  <h3 className="text-lg font-bold text-blue mb-3">
                    Receiver Details
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { label: "Name", value: selectedShipment.receiver_name },
                      { label: "Unit", value: selectedShipment.receiver_unit },
                      {
                        label: "Email",
                        value: selectedShipment.receiver_email,
                      },
                      { label: "City", value: selectedShipment.receiver_city },
                      {
                        label: "Street",
                        value: selectedShipment.receiver_street,
                      },
                      {
                        label: "Country",
                        value: selectedShipment.receiver_country,
                      },
                      {
                        label: "Phone",
                        value: selectedShipment.receiver_phone,
                      },
                      {
                        label: "Building",
                        value: selectedShipment.receiver_building,
                      },
                    ].map((item, index) => (
                      <div key={index} className="flex flex-col">
                        <span className="text-sm text-gray-600">
                          {item.label}
                        </span>
                        <span className="text-base font-bold text-gray-900">
                          {item.value || "-"}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Shipment Details */}
                <section className="bg-blue-50 border border-blue/30 rounded-lg p-4">
                  <h3 className="text-lg font-bold text-blue mb-3">
                    Shipment Details
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      {
                        label: "Shipment ID",
                        value: selectedShipment.shipment_id,
                      },
                      { label: "Job Order", value: selectedShipment.job_order },
                      { label: "Type", value: selectedShipment.shipment_type },
                      { label: "Origin", value: selectedShipment.shipper_city },
                      {
                        label: "Destination",
                        value: selectedShipment.receiver_city,
                      },
                      { label: "Status", value: selectedShipment.status },
                      {
                        label: "Created By",
                        value: selectedShipment.created_by,
                      },
                      {
                        label: "Description",
                        value: selectedShipment.shipment_description,
                      },
                      {
                        label: "Shipment Code",
                        value: selectedShipment.shipment_code,
                      },
                      {
                        label: "ETD",
                        value: selectedShipment.etd
                          ? new Date(selectedShipment.etd).toLocaleString(
                              "en-US",
                              {
                                timeZone: "Asia/Manila",
                                hour: "numeric",
                                minute: "numeric",
                                month: "long",
                                day: "numeric",
                                year: "numeric",
                                hour12: true,
                              }
                            )
                          : "-",
                      },
                      {
                        label: "ETA",
                        value: selectedShipment.eta
                          ? new Date(selectedShipment.eta).toLocaleString(
                              "en-US",
                              {
                                timeZone: "Asia/Manila",
                                hour: "numeric",
                                minute: "numeric",
                                month: "long",
                                day: "numeric",
                                year: "numeric",
                                hour12: true,
                              }
                            )
                          : "-",
                      },
                    ].map((item, index) => (
                      <div key={index} className="flex flex-col">
                        <span className="text-sm text-gray-600">
                          {item.label}
                        </span>
                        <span className="text-base font-bold text-gray-900">
                          {item.value || "-"}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
              </div>

              {/* Footer Buttons */}
              <div className="flex justify-center mt-6">
                <button
                  onClick={closeDetailsModal}
                  className="bg-blue text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition shadow-md"
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          portalContainer
        )}
    </div>
  );
};

export default ShipmentTable;
